/**
 * ChartGroupView — encapsulates the SciChart surface lifecycle for one chart
 * group AND its data feed. Builds the group via SciChartJsChartBuilder, then
 * drives it through the transport interface: a FixtureTransportClient supplies a
 * bulk load and a few realtime append frames, mapped onto the chart via
 * chart-feed. Disposes the client + surface on unmount / before each rebuild.
 *
 * The fixture is a drop-in for the live QvWebSocketClient — swapping in live WS
 * + Arrow data is a client construction change here, nothing else.
 *
 * It also **shows whether the feed is connected**, which is not decoration: a
 * socket that dropped fires none of the data callbacks, so a chart with a dead
 * feed is pixel-for-pixel a chart in a quiet market. See `feed` below.
 *
 * **Rebuilds are keyed on structure, not on the `group` object.** A rebuild
 * destroys a WebGL/WASM surface and refetches the data, so it must happen only
 * when the chart's shape actually changed. Pane heights are explicitly excluded
 * (see {@link structuralKey}): they are applied by repositioning the SubCharts,
 * and letting a divider release rebuild the surface churned it on every drag.
 *
 * The **theme is excluded for the same reason** — and deliberately not folded
 * into `structuralKey`, which would trade a repaint for a full WebGL teardown
 * plus a refetch on every theme switch. It arrives through `builder.applyTheme`
 * from its own effect below, exactly as pane heights do.
 *
 * Resize note: SciChart auto-fits its container, so grid resizing the host cell
 * is enough — no rebuild needed for size changes.
 *
 * **Several of these may be mounted at once**, and what makes that safe is the
 * `id` on the div handed to SciChart. Surfaces share one WASM context, and
 * SciChart keys each one's render destination off that div's id — so charts
 * whose divs have no id all claim the same destination and each new surface
 * silently deletes the previous one's. See the comment on the div itself.
 */

import {
  cachedAxisSpecs,
  cachedCatalog,
  cachedEventKinds,
  loadIndicatorCatalog,
} from "../api/indicators";
import { onYAxisMode, publishYManual } from "../lib/y-axis-mode";
import { withProjectedTwins } from "../lib/add-indicator";
import { annotationsFor } from "../lib/workspace-actions";
import { geometryAnnotations } from "../lib/geometry-annotations";
import { SEAS_BANDS_LAYER, seasBandSpans } from "../lib/seas-bands";
import { hitsAt, hitsAxisId, hitsRange, isSeasHitsAlias } from "../lib/seas-hits";
import type { AnnotationTool } from "./AnnotationTools";
import { AnnotationStyleBar } from "./AnnotationStyleBar";
import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { EstimateLine, EventMarker, PlotHit } from "@qufolio/chart-engine";
import {
  SciChartJsChartBuilder,
  barSlot,
  indexAtTime,
  legendColumn,
  legendName,
  offsetAtTime,
  resolveInteractions,
  timeAtOffset,
} from "@qufolio/chart-engine";
import type { Annotation, ChartGroup, Frequency, Pane } from "@qufolio/model";
import {
  FixtureTransportClient,
  QvWebSocketClient,
  type ConnectionStatus,
  type GeometryPayload,
  type LiveInfo,
  type TransportListeners,
} from "@qufolio/transport";
import { join as joinChartHub } from "../lib/chart-hub";
import { joinWindowLink, publishWindow } from "../lib/window-link";
import { joinCrosshairLink, publishCrosshair } from "../lib/crosshair-link";
import { publishChartTable, type ExportSeries } from "../lib/chart-export";
import {
  defaultCandleStyle,
  useDefaultCandleStyle,
  withCandleDefault,
  type CandleStyle,
} from "../lib/candle-style";
import { chartsLicensed, type ChartsVerdict } from "../lib/scichart-bootstrap";
import { ChartsUnavailable } from "./ChartsUnavailable";
import {
  toIndicatorRequest,
  bulkDeltas,
  appendDeltas,
  applyPipDeltas,
  pipBars,
  pipChartGroupIdInt,
  realBarCount,
  type PipBar,
} from "../lib/chart-feed";
import {
  SPAN_LABELS,
  indexAtOrAfter,
  parseDateInput,
  planDate,
  planSpan,
  rightGap,
  barsInWindow,
  panStep,
  rightMarginOf,
  spanStart,
  visibleBarsOf,
  windowAround,
  windowEndingAt,
  windowHoldingRight,
  windowPannedBy,
  zoomedBars,
  type SpanPreset,
} from "../lib/chart-window";
import {
  DEFAULT_SPAN_YEARS,
  DEFAULT_TIMINGS,
  previewFrame,
  previewTarget,
} from "../lib/history-preview";
import { priceBottomFraction, priceTopFraction, pricePlot } from "../lib/chart-group";
import { movePane, paneAtFraction } from "../lib/pane-order";
import {
  earningsMarkers,
  growthFor,
  newsMarker,
  NEXT_EARNINGS_PREFIX,
  quarterFor,
  upcomingEarnings,
  upcomingMarker,
  estimateLines,
  ESTIMATED_SERIES,
  type UpcomingEarnings,
} from "../lib/chart-events";
import { loadFundamentals } from "../lib/symbol-fundamentals";
import { loadNews } from "../lib/symbol-news";
import type { EarningsQuarter } from "../api/symbols";
import { EventPopup } from "./EventPopup";
import { LevelsPopup } from "./LevelsPopup";
import { intervalLabel } from "../lib/interval-label";
import {
  INTRADAY_FIRST_BARS,
  MAX_INTRADAY_BARS,
  barOver,
  countdown,
  formingBand,
  isIntraday,
  liveState,
  periodSeconds,
  planPanLoad,
  timeLabelsFor,
  type LiveState,
} from "../lib/intraday";
import { publishLive } from "../lib/live-badge";
import { debugBuilder } from "../lib/chart-debug";
import { LiveBadge } from "./LiveBadge";
import { isComposite } from "../lib/composite";
import { isIndexSymbol, symbolTitle } from "../lib/topdown";
import { useCompanyProfile } from "../lib/symbol-profile";
import { registerChartImage, registerChartSnapshot, type ChartSnapshot } from "../lib/chart-snapshot";
import { PaneDividers } from "./PaneDividers";
import { PaneReorder, PANE_TOOLBAR_BOTTOM } from "./PaneReorder";
import { ChartDataInspector, type ChartDataSnapshot } from "./ChartDataInspector";
import type { ChartTimingRecord } from "../lib/chart-timing";
import { ValueLegend, type LegendControls } from "./ValueLegend";
import { PlotChip, type PlotChipState } from "./PlotChip";
import { useIsNarrow } from "../lib/use-narrow";
import { ChartPip } from "./ChartPip";
import { available } from "../lib/features";
import { nextCoarser, pipFrequencyFor, swapWithChart, type PipPatch } from "../lib/pip";
import { useTheme, useTokenResolver } from "./theme";
import { idToken } from "../lib/session";
import { currentPass } from "../lib/pass";
import { actingAs } from "../lib/act-as";
import { wsUrl } from "../api/backend";
import { barIntelFor, barIntelPlot } from "../lib/bar-intelligence-view";
import {
  INSIDE_OUTSIDE_LAYER,
  insideOutsideHighlights,
  insideOutsideKinds,
  isBarHighlightAlias,
  kindLabel,
  type BarKind,
} from "../lib/inside-outside";
import { publishBarIntel } from "../lib/bar-intel-store";

/** Live qv-data WebSocket URL, e.g. ws://127.0.0.1:8000/ws/qv. Empty → offline fixture. */
// The chart socket, under whichever backend is selected. `wsUrl` derives the
// scheme from it — an override of https://qf-us.fly.dev must produce `wss:`, and
// a `ws:` socket against an HTTPS page is refused as mixed content with an error
// naming neither the scheme nor the setting.
const QV_WS_URL = wsUrl("/ws/qv", import.meta.env.VITE_QV_WS_URL);

/**
 * What the inspector shows before the first load lands — an honest empty rather
 * than a hidden button, so "the data never arrived" and "there is no button" are
 * not the same picture.
 */
const EMPTY_SNAPSHOT: ChartDataSnapshot = {
  chartGroupId: "",
  status: "ok",
  xKind: "seconds",
  x: [],
  columns: {},
  requested: [],
  timings: {},
};

/**
 * The clock the client-side stages are measured on.
 *
 * `performance.now` because it is monotonic — the system clock can be adjusted
 * mid-session and a stage that came out negative would read as a bug in the
 * panel rather than in the reading. Guarded for the hosts that lack it (a
 * WebView, a test environment): a missing global must not be able to fail a
 * chart over a measurement.
 */
function perfNow(): number {
  return typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();
}

/** A transient request to put one date on screen. Nothing persists it (D8). */
export interface GoToDate {
  /** `yyyy-mm-dd`, as a date input hands it over. */
  date: string;
  /**
   * Bumped per request, and the only thing the effect keys on. Asking for the
   * same date twice is two requests — the second may follow a pan away from it,
   * and a chart that ignored it would look broken in exactly the case the box
   * exists for.
   */
  nonce: number;
}

export interface ChartGroupViewProps {
  group: ChartGroup;
  /** Layout id, echoed into the transport request. */
  layoutId: string;
  /**
   * Draw the LIVE badge over the chart's top-right corner. Only where there is
   * no toolbar to carry it — the phone's pager. On the desktop it sits in the
   * toolbar left of ⛶ (owner, 2026-09-29), read from `lib/live-badge.ts`, which
   * this view publishes to either way.
   */
  liveBadgeOnCanvas?: boolean;
  /**
   * Pane heights settled after a divider drag. Omit to render without dividers —
   * the chart is then read-only in that dimension.
   */
  onPaneHeights?: (heights: string[]) => void;
  /**
   * The panes were restacked — one slot, from the on-chart arrows.
   *
   * Panes rather than heights, because a reorder is a permutation of the array
   * and the heights ride along on the pane objects. It arrives as the finished
   * array for `setPanes`, the same shape the indicator picker hands back, so
   * this view never decides what a move means.
   */
  onPaneOrder?: (panes: Pane[]) => void;
  /**
   * Which company events to mark along the foot of the price pane.
   *
   * Passed in rather than read from the group because they are **view state**
   * today (D8) — see the note on `eventMarkers` below.
   */
  events?: { earnings: boolean; news: boolean };
  /**
   * A span preset chose a new window, and possibly a bigger load.
   *
   * Both in one call because they belong to one decision: a year of five-minute
   * bars is a window *and* a request, and dispatching them apart would render a
   * chart with a year-wide window over a fortnight of data.
   */
  onWindow?: (window: { visibleBars?: number; barsRange?: number }) => void;
  /**
   * The layout's "Date range" link, when it is on: a wheel zoom or a pan here
   * moves every other chart carrying the same key, by time. See
   * `lib/window-link.ts`.
   */
  windowLink?: string;
  /**
   * The layout's crosshair link, when it is on (it defaults on): the date under
   * the pointer here is drawn on every other chart carrying the same key, and
   * theirs here. See `lib/crosshair-link.ts`.
   */
  crosshairLink?: string;
  /**
   * One edit to this chart's picture-in-picture panel.
   *
   * A patch rather than four callbacks, the shape `IndicatorPicker`'s `onPlot`
   * already uses: a change carries one key, and threading four props through the
   * grid to say so would be four places for the next one to be forgotten.
   */
  onPip?: (patch: PipPatch) => void;
  /**
   * Suppress the panel without touching what it asked for.
   *
   * The two-chart rule hides the *active* chart's PIP so the panel is never over
   * the chart just clicked. Visibility only: the subscription stays, because
   * dropping it would make clicking between charts cost a refetch each way.
   */
  pipHidden?: boolean;
  /** Centre on a date. Transient — see {@link GoToDate}. */
  goTo?: GoToDate;
  /**
   * The armed drawing tool, or `null`.
   *
   * View state (D8) owned by `App`, because the palette is one strip serving
   * whichever chart is active — a tool held per chart would re-arm itself when
   * the ring came back round.
   */
  drawingTool?: AnnotationTool | null;
  /** The armed financial-tools annotation type, or `null`. Placed by its own modifier. */
  tradingTool?: string | null;
  /**
   * A drawing finished. The X values are timestamps, and the annotation is the
   * user's — derived ones never come out of the builder.
   */
  onAnnotationDrawn?: (paneId: string, annotation: Annotation) => void;
  /**
   * The trading plan's lines and the fills, computed by the host per render.
   *
   * Passed in rather than fetched here, because the journal is one document and
   * every chart on a layout would otherwise fetch it separately. They go to the
   * **price** pane and arrive as `derived`, so they carry no grips and are
   * never collected back into `Pane.annotations`.
   */
  journalAnnotations?: readonly Annotation[];
  /** Which of those may be dragged — a plan's stop and targets. */
  journalEditable?: ReadonlySet<string>;
  /** A plan level was dragged. The host amends the plan; nothing is stored here. */
  onLevelMoved?: (annotationId: string, price: number) => void;
  /**
   * A plan level was right-clicked.
   *
   * Reported rather than handled, because the menu belongs to the app — this
   * component knows *which line* was hit and nothing about what a plan can do.
   * Returning false-y lets the event bubble to the ordinary chart menu.
   */
  onLevelMenu?: (annotationId: string, x: number, y: number) => void;
  /**
   * What the value legend's rows can do, or omitted for a read-only one.
   *
   * Built by `App`, because acting on a plot is a workspace dispatch and the
   * family grouping needs the indicator catalog — neither of which this
   * component has or should acquire to render a number.
   */
  legendControls?: LegendControls;
  /**
   * A drawing gesture has a first point placed, waiting for its second click —
   * or no longer has.
   *
   * The host needs it for one thing: with a point down, Escape takes the point
   * back and the tool **stays armed**; with none, Escape disarms. Cancelling is
   * done here, because only this component knows which chart the point is in;
   * the host only has to know not to disarm.
   */
  onPlacing?: (placing: boolean) => void;
  /** Delete was pressed with drawings selected. The host removes them. */
  onAnnotationsDeleted?: (annotationIds: readonly string[]) => void;
  /**
   * A selected drawing was restyled from the style bar.
   *
   * A **patch**, not a whole style: the bar edits one control at a time while
   * showing the current values, so it genuinely means "this key, that value".
   * See `styleAnnotation` for why that differs from a plot's style.
   */
  onAnnotationStyled?: (annotationId: string, style: NonNullable<Annotation["style"]>) => void;
  /**
   * A trade plan was drawn: entry price, then stop price.
   *
   * Two numbers, because everything else about a plan — direction, size,
   * targets — is arithmetic the host already does for the form. A chart that
   * computed them would be a second place the sizing lived.
   */
  onPlanDrawn?: (entry: number, stop: number, at: number) => void;
  /**
   * Bumped by the host to clear the placed drawings.
   *
   * A nonce rather than a callback the host holds: clearing has to reach the
   * *surface*, which only this component's builder can touch, and the contract
   * half is the host's own dispatch. One number keeps the two halves of one
   * action together without publishing the builder upwards.
   */
  clearNonce?: number;
  /**
   * The last close and the symbol it belongs to.
   *
   * The symbol travels with it because several charts publish into one map — a
   * price without its symbol can only ever describe whichever chart loaded
   * last, which is wrong the moment a layout has two.
   */
  onLastPrice?: (symbol: string, price: number, at: number) => void;
}

/** Where the window's right edge goes when it is (re)applied. */
type Anchor =
  /** The latest bar — a fresh load, a preset, jump-to-latest. */
  | { at: "end" }
  /** Wherever it already is — the count box, which must not teleport. */
  | { at: "hold" }
  /** Centred on one bar — the date box. */
  | { at: "bar"; index: number }
  /** Shifted along, same width — the ‹ and › buttons. */
  | { at: "pan"; direction: -1 | 1 };

/**
 * A signature of everything about a chart group that requires a **rebuild**,
 * deliberately excluding `pane.height` and every plot's `style`/`palette`.
 *
 * Pane heights are applied by repositioning the SubCharts, so treating a height
 * change as structural would tear down and recreate the WebGL surface — and
 * refetch the data — every time a divider is released. Keying the effect on this
 * instead of on the `group` object is the same fix as the value-keyed column
 * memo in `ScreenerGrid`: an object identity must not drive an expensive effect.
 *
 * **Plot style and palette are excluded for exactly the same reason**, now that
 * the indicator picker can change a colour, a width or a dash on a plot already
 * on the chart. Those are property writes on series objects that already exist
 * (`builder.applyStyle`), and folding them in would trade a repaint for a WebGL
 * teardown plus a refetch on every drag of a width spinner. Plot `type` stays in:
 * a candlestick and a line are different renderable classes over different data
 * shapes, so that one genuinely is structural.
 *
 * **`visibleBars` and `rightMargin` are excluded, and `barsRange` is not**, which
 * is the whole shape of chart navigation in one line. Moving the window over
 * bars that are already loaded is an axis property write; asking for bars that
 * were never loaded is a different request, so raising `barsRange` *should*
 * rebuild — and does, through this key, with no machinery of its own.
 */
export function structuralKey(group: ChartGroup): string {
  return JSON.stringify({
    id: group.id,
    symbol: group.symbol,
    frequency: group.frequency,
    barsRange: group.barsRange,
    // Shapes the *request*, like `barsRange`: the server re-forms the bars and
    // computes every indicator over them, so the same symbol at the same
    // interval is a different table. It rebuilds today anyway because switching
    // moves the price plot's alias, but relying on that would make this correct
    // by accident — and a workspace loaded straight onto Heikin-Ashi has no
    // alias *change* to notice.
    barSource: group.barSource,
    // **The PIP's request, not the PIP's appearance.** Its chart view is a
    // second chart group on the same socket, so whether that group exists and
    // what interval it asks for are both structural — a rebuild is what puts
    // the new request on the wire. `pipCorner` and `pipSize` are deliberately
    // absent: both are repaints of a panel already holding its data, and
    // folding them in would cost a WebGL teardown plus a refetch of the *chart*
    // every time somebody nudged the panel a corner over.
    //
    // The view is reduced to a boolean rather than carried whole, for the same
    // reason: switching the panel from Profile to Peers changes nothing on the
    // socket, and only the step in or out of `chart` does.
    pipChart: group.pipView === "chart",
    pipFrequency: group.pipView === "chart" ? group.pipFrequency : undefined,
    xAxis: group.xAxis,
    // `annotations` is dropped for the same reason `height`, `style` and
    // `palette` are: it is drawn by `setAnnotations`, which is a repaint. Left
    // in the key — which it was, by falling into `...rest` — every drawing
    // costs a WebGL teardown and a full refetch, so drawing five lines would
    // reload the chart five times and each new line would appear after a flash.
    // An axis **range** is dropped for that same reason, while the axis itself
    // stays: adding one is structural — a new pane with a new scale — but
    // pinning an existing one to 0-100 is a property write. Left in, nudging a
    // minimum would tear down the surface and refetch every bar, so a typed
    // range would reload the chart once per digit.
    // **Sorted by id, and `order` dropped, so pane *position* is not in the key.**
    // Reordering the stack changes nothing about the request — same symbol, same
    // bars — so it is `setPaneOrder`, a reposition, exactly as a divider drag is
    // `setPaneHeights`. Left positional, moving a pane up three slots would cost
    // three WebGL teardowns and three refetches, and the zoom window would snap
    // back on each one. `order` goes with it because it is written at creation
    // and read by nowhere (docs/TODO.md); left in `...rest` it would put
    // position back into the key through the side door.
    panes: [...group.panes]
      .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
      .map(({ height: _h, annotations: _a, order: _o, plots, axes, ...rest }) => ({
      ...rest,
      axes: axes?.map(({ autoRange: _ar, visibleRange: _vr, ...axis }) => axis),
      // `hidden` joins them: showing a plot again must not cost a WebGL
      // teardown and a refetch, and hiding one must not throw away the bars it
      // would need the moment it comes back. `setPlotHidden` is a property
      // write on a series that already exists.
      plots: plots?.map(
        ({ style: _style, palette: _palette, hidden: _hidden, ...plot }) => plot,
      ),
    })),
  });
}

/**
 * Every axis id with the range it is pinned to, flattened across panes.
 *
 * The mirror of `stylesOf`: what was taken out of `structuralKey` has to come
 * back as its own key, or the change would simply never reach the surface.
 * `undefined` is "let it fit", which is what `AxisInfo` means by an absent
 * `visibleRange`.
 */
const axisRangesOf = (group: ChartGroup) =>
  group.panes.flatMap((pane) =>
    (pane.axes ?? []).map((axis) => ({
      id: axis.id,
      // `always` beside an explicit range means the renderer refits over it, so
      // the two together are read as unpinned — the same rule `withPaneScale`
      // enforces on the way in, applied here on the way out.
      range:
        axis.autoRange === "always" || !axis.visibleRange
          ? undefined
          : axis.visibleRange,
    })),
  );

/** Pane heights as `setPaneHeights` wants them, in display order. */
const heightsOf = (group: ChartGroup): string[] =>
  group.panes.map((p) => p.height ?? "1fr");

/**
 * Each pane's height keyed by **its own id**, sorted — so the key says what the
 * heights *are* and not what order they are in.
 *
 * `heightsOf(group).join("|")` was the obvious spelling and coupled two
 * unrelated things: swapping a `3fr` price pane with a `1fr` volume pane rewrote
 * the sequence without changing a single height, firing the heights effect for a
 * gesture that was purely a reorder. That is the case the ordering of the two
 * effects has to survive, so the better fix is for it not to arise — a pure
 * reorder now leaves this string alone and only `setPaneOrder` runs.
 *
 * A divider drag still moves it, because a height genuinely changed.
 */
export function heightsKeyOf(group: ChartGroup): string {
  return group.panes
    .map((pane) => `${pane.id}:${pane.height ?? "1fr"}`)
    .sort()
    .join("|");
}

/**
 * Every plot's id with its style and palette, flattened across panes — with
 * the viewer's default candle style filled in where a candlestick has not
 * picked one (`lib/candle-style.ts`), so a JAS member's candles restyle to
 * hollow the moment their groups arrive, without a rebuild or a save.
 */
const stylesOf = (group: ChartGroup, candleDefault: CandleStyle) =>
  withCandleDefault(group.panes, candleDefault).flatMap((pane) =>
    (pane.plots ?? []).map((plot) => ({
      id: plot.id,
      style: plot.style,
      palette: plot.palette,
    })),
  );

/**
 * Every plot's id with whether it is drawn.
 *
 * Its own key rather than a field of `stylesOf`, for the reason it is its own
 * builder method: hiding is not a restyle. Folding it in would re-apply every
 * plot's colour on a visibility toggle and, worse, re-apply every plot's
 * visibility on a colour change — and `applyStyle` has nothing to say about
 * visibility, so the second one would be a silent no-op that looked wired up.
 */
const hiddenOf = (group: ChartGroup) =>
  group.panes.flatMap((pane) =>
    (pane.plots ?? []).map((plot) => ({ id: plot.id, hidden: plot.hidden ?? false })),
  );

/**
 * One button in the bottom-right strip.
 *
 * A module-level constant rather than a style prop per button: these are
 * siblings and have to stay the same size, and the strip is meant to grow.
 * Colour comes from CSS variables so a theme switch repaints it without this
 * file knowing a theme exists (D13).
 */
/** Price columns, which are the chart rather than an indicator on it. */
const SNAPSHOT_BASE_COLUMNS = new Set(["open", "high", "low", "close", "volume"]);

/**
 * How many closes describe the shape of a window.
 *
 * Enough to show a base, a breakout or a lower high; few enough that the series
 * costs a few hundred tokens rather than several thousand. A 150-bar window
 * thins to every fifth bar, which is the resolution an eye reads off a chart
 * anyway.
 */
const CLOSE_PATH_POINTS = 32;

const STRIP_BUTTON: CSSProperties = {
  width: 20,
  height: 20,
  padding: 0,
  borderRadius: "50%",
  border: "1px solid var(--qf-border)",
  background: "var(--qf-surface)",
  color: "var(--qf-text-secondary)",
  font: "inherit",
  fontSize: 12,
  lineHeight: "18px",
  cursor: "pointer",
};


export function ChartGroupView({
  group: stored,
  layoutId,
  liveBadgeOnCanvas = false,
  onPaneHeights,
  onPaneOrder,
  events,
  onWindow,
  windowLink,
  crosshairLink,
  onPip,
  pipHidden,
  goTo,
  drawingTool = null,
  tradingTool = null,
  onAnnotationDrawn,
  journalAnnotations,
  journalEditable,
  onLevelMoved,
  onLevelMenu,
  legendControls,
  onPlacing,
  onAnnotationsDeleted,
  onAnnotationStyled,
  onPlanDrawn,
  clearNonce = 0,
  onLastPrice,
}: ChartGroupViewProps) {
  /**
   * How many entries the fetched catalog holds, as a signal rather than as data.
   *
   * The catalog decides which families are projectable, and it arrives *after*
   * the first render — so the fold below has to re-run when it lands. A count
   * rather than the array itself: the array is held in a module-level cache and
   * never mutated, so a length is enough to say "it changed" and costs no copy.
   */
  const [catalogSize, setCatalogSize] = useState(() => cachedCatalog().length);

  /**
   * The chart as it is actually drawn: the stored group plus, when projection is
   * on, one yellow twin per projectable indicator.
   *
   * **Folded once, here, and everything downstream reads it.** The request
   * builder, the render plan, the value legend and `structuralKey` all derive
   * from `panes`, so folding at the top means none of them has to know this
   * feature exists — and none of them can be the one that forgot. The failure
   * that would produce is a plot drawn and never fed, or fed and never drawn.
   *
   * Returns the stored group **by identity** when there is nothing to add, so a
   * chart without projection re-renders and rebuilds exactly as often as before.
   */
  const projected = useMemo(
    () => withProjectedTwins(stored, cachedCatalog()),
    // `catalogSize` is the signal, not an input — see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stored, catalogSize],
  );

  /**
   * **An intraday chart's loaded count is this view's, never the workspace's.**
   * It starts at `INTRADAY_FIRST_BARS` and a pan or a span preset raises it;
   * nothing is saved, so the next open starts small again. Reset when the
   * symbol or interval changes — a new chart, a new first load.
   *
   * Folded into `group` like the projected twins, so the request, the
   * structural key and the window arithmetic all read one number.
   */
  const [sessionBars, setSessionBars] = useState<number | undefined>(undefined);
  const loadKey = `${stored.symbol}|${stored.frequency}`;
  const [loadKeySeen, setLoadKeySeen] = useState(loadKey);
  if (loadKeySeen !== loadKey) {
    setLoadKeySeen(loadKey);
    setSessionBars(undefined);
  }
  const group = useMemo(
    () =>
      isIntraday(projected.frequency)
        ? { ...projected, barsRange: sessionBars ?? INTRADAY_FIRST_BARS }
        : projected,
    [projected, sessionBars],
  );

  const theme = useTheme();
  // For the style bar's swatches. Memoised on the theme in the hook, so the
  // bar's own memos are stable between theme switches.
  const resolveToken = useTokenResolver();
  const containerRef = useRef<HTMLDivElement>(null);
  // Through a ref so the load effect does not take the callback as a dependency
  // — it changes identity on every parent render, and depending on it would
  // rebuild the surface and refetch whenever anything above re-rendered.
  const lastPriceRef = useRef(onLastPrice);
  lastPriceRef.current = onLastPrice;
  // Held so a divider drag can reposition the panes without going through the
  // workspace, which would rebuild the surface on every pointer move.
  const builderRef = useRef<SciChartJsChartBuilder | null>(null);
  /** Undo for the snapshot registry entry, cleared with the surface. */
  const unregisterSnapshot = useRef<(() => void) | null>(null);
  const unregisterImage = useRef<(() => void) | null>(null);
  /**
   * Whether the chart may be *shown*. `null` while SciChart is still deciding.
   *
   * **Visibility rather than construction, and that is not the obvious design.**
   * SciChart draws its licence notice at construction, so withholding the
   * surface looks like the only safe move — and it deadlocks: `applyLicense`
   * runs over `getAllLicenseContexts()`, which reads the list of surfaces, so
   * with no surface the licence flow never runs and the verdict never arrives.
   * Measured on the first production deploy; see `chartsLicensed`.
   *
   * So the surface is built and an opaque cover sits over it until the answer
   * comes back. The cover is committed in the same render as the container and
   * the surface is only created in an effect afterwards, so there is no frame in
   * which SciChart's notice is visible.
   */
  const [licence, setLicence] = useState<ChartsVerdict | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** A refusal from a control — a span past the ceiling, a date too far back. */
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * An intraday chart has asked for older bars and the rebuild carrying them
   * has not landed. One at a time: the window keeps moving while the request is
   * out, and every move would otherwise ask again. Cleared by the load, and by
   * the teardown the request itself causes.
   */
  const loadingMoreRef = useRef(false);
  /** Read by the window listener, which is built once per surface. */
  const onWindowRef = useRef(onWindow);
  onWindowRef.current = onWindow;
  /** Read by the same listener, so ticking the link never rebuilds the surface. */
  const windowLinkRef = useRef(windowLink);
  windowLinkRef.current = windowLink;

  // Join the layout's "Date range" link. Applied through `builderRef` rather
  // than captured, because a rebuild replaces the builder and not this channel.
  useEffect(() => {
    if (!windowLink) return;
    return joinWindowLink(windowLink, stored.id, (window) =>
      builderRef.current?.setBarWindow(window),
    );
  }, [windowLink, stored.id]);

  /** Read by the hover listener, which is attached once per builder. */
  const crosshairLinkRef = useRef(crosshairLink);
  crosshairLinkRef.current = crosshairLink;

  // Join the layout's crosshair link — `windowLink`'s shape, applied through
  // `builderRef` for the same reason. Leaving clears any line a peer left
  // here, or unticking the link would strand the last one on screen.
  useEffect(() => {
    if (!crosshairLink) return;
    const leave = joinCrosshairLink(crosshairLink, stored.id, (time) =>
      builderRef.current?.showLinkedTime(time),
    );
    return () => {
      leave();
      builderRef.current?.showLinkedTime(null);
    };
  }, [crosshairLink, stored.id]);

  /**
   * Ask for more history: the view's own count on an intraday chart, the
   * workspace's `barsRange` otherwise (a daily chart is sent its whole file
   * and rarely gets here). See `sessionBars`.
   */
  const raiseBars = useCallback((barsRange: number, visibleBars?: number) => {
    if (isIntraday(groupRef.current.frequency)) {
      setSessionBars(barsRange);
      if (visibleBars !== undefined) onWindowRef.current?.({ visibleBars });
    } else {
      onWindowRef.current?.({ barsRange, ...(visibleBars !== undefined ? { visibleBars } : {}) });
    }
  }, []);
  /**
   * Whether the feed is actually connected.
   *
   * Held because **a chart with no feed looks exactly like a chart in a quiet
   * market**: the transport's close fires none of the data callbacks, so before
   * this the bars simply stopped and nothing said so. A qv-data restart during
   * the session left charts on the previous day's close while the server was
   * serving the current one — stale prices, no error, no recovery. The client
   * now reconnects underneath (see `QvWebSocketClient`), but the gap is still a
   * gap and only the view can show it.
   *
   * Starts `idle` rather than `connecting`, so nothing is claimed before a
   * client exists to claim it.
   */
  const [feed, setFeed] = useState<ConnectionStatus>("idle");
  /**
   * An intraday chart's live facts from the server's latest frame (`onLive`),
   * and a one-second tick while it is live — which moves the countdown, the
   * band and the dot's pulse without touching anything else.
   */
  const [liveInfo, setLiveInfo] = useState<LiveInfo | null>(null);
  /**
   * The shapes on this chart's latest accepted frame (`onGeometry`) —
   * trendlines now, chart patterns later. Replaced whole by every frame that
   * carries them and dropped with the surface, like `liveInfo`.
   * `lib/geometry-annotations.ts` turns them into derived drawings.
   */
  const [geometry, setGeometry] = useState<GeometryPayload | null>(null);
  const [liveTick, setLiveTick] = useState(0);

  /**
   * Bar timestamps, unix seconds, ascending — the X values the feed delivers.
   *
   * A **ref**, not state, because it is up to five thousand numbers that gain
   * one on every realtime append: copying the array into state per bar would
   * re-render the chart sixty times an hour to change a fact nothing draws. What
   * the controls actually need from it is one number, `barsLoaded` below, which
   * only moves on a bulk load.
   */
  const xRef = useRef<number[]>([]);
  /**
   * How many of `xRef` are real bars, as against a projection past the last one.
   *
   * `xRef` deliberately holds **both**: the axis places a drawing or a crosshair
   * against these timestamps, and a seasonal cone reaching into November has to
   * be placeable and hoverable like anything else. But almost every *control*
   * means the bars — "go to the end" means the last candle, "how far back does a
   * year reach" is measured over sessions that happened, and the last price is a
   * price. So the array is whole and the count is what the controls read.
   *
   * Equal to `xRef.current.length` whenever nothing is projected, which is every
   * chart that has no seasonality plot on it.
   */
  const realBarsRef = useRef(0);
  /** Inside / Outside: each painted bar's kind by its time, for the value legend. */
  const barKindsRef = useRef<Map<number, BarKind>>(new Map());
  /**
   * The last load was every bar the symbol has (`qv.complete` — daily). Then a
   * date or a span before the first bar goes *to* the first bar instead of
   * raising `barsRange` for history that does not exist. Set and cleared beside
   * `realBarsRef`, for the same reason: it describes the load, not the chart.
   */
  const completeRef = useRef(false);
  /**
   * The scalar string columns the last load carried, by name.
   *
   * One reader today: `seasYears`, the number of complete years a seasonal
   * reading was actually built from. It cannot come from the alias — the alias
   * says what was *asked* for — and it cannot be derived here, since only the
   * server knows how much history the symbol has.
   */
  const labelsRef = useRef<Record<string, string>>({});
  const [barsLoaded, setBarsLoaded] = useState(0);
  /**
   * Bumped when a load lands, so the annotation effect re-runs against bars
   * that now exist. A drawing whose bars were not loaded is not drawn at all
   * (rather than clamped to the edge), so it has to be offered again once they
   * arrive or it stays missing until something else happens to change.
   */
  const [barStamp, setBarStamp] = useState(0);
  /**
   * Bumped on a realtime tick while a Bar Intelligence plot is on the chart —
   * its distances are measured against the live bar, so they move with every
   * tick where every other derived drawing moves only with a new bar.
   */
  const [tickStamp, setTickStamp] = useState(0);
  /**
   * A date asked for before the loaded history reached it, held across the
   * refetch it triggered.
   *
   * Without this the sequence is: type 2019, raise `barsRange`, refetch, and
   * land on the default window — the request answered correctly and the chart
   * showing the wrong thing. The load applies it and clears it.
   */
  const pendingCenterRef = useRef<number | undefined>(undefined);
  /**
   * Empty index space in the window as it was last written.
   *
   * Kept because the right margin can change *between* two applications, and
   * holding the right edge has to subtract the gap that is actually there
   * rather than the one the current setting implies.
   */
  const appliedGapRef = useRef(0);
  /**
   * The decoded columns as they arrived, for the `i` inspector.
   *
   * A **ref** for `xRef`'s reason — it is every column the load carried, and
   * copying that into state on each realtime append would re-render the chart to
   * change a fact nothing draws. `dataStamp` below is what the inspector renders
   * against, and it only moves while the inspector is open.
   *
   * Copied rather than aliased: the decoded object belongs to the transport, and
   * the realtime path appends to this one.
   */
  const dataRef = useRef<ChartDataSnapshot | null>(null);
  const [inspecting, setInspecting] = useState(false);
  const [dataStamp, setDataStamp] = useState(0);
  // Read by the feed listeners, which are built once per build and would
  // otherwise close over whichever value `inspecting` had at that moment.
  const inspectingRef = useRef(false);
  inspectingRef.current = inspecting;

  /**
   * The bar under the pointer, for the value legend.
   *
   * **Mirrored in a ref, and that is the whole performance story.** The handler
   * fires on every pointer move — sixty a second — while the *index* changes
   * only when the pointer crosses a bar boundary, which at any readable zoom is
   * every several pixels. Comparing here and returning is what keeps a hover
   * from being a re-render per frame; `setState` with an equal value bails out
   * of the commit but not reliably out of the render.
   */
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const hoverRef = useRef<number | null>(null);
  // Same reason as `inspectingRef`: the handler is installed once per build and
  // would otherwise hold whichever value the setting had at that moment.
  const legendOnRef = useRef(true);
  legendOnRef.current = group.valueLegend ?? true;

  /**
   * The line under the pointer (charts.md, "The line under the pointer").
   *
   * The bar is kept here whether or not the legend is on, since the chip reads
   * at it too; `onHoverTime` reports before `onHoverPlot` on the same move. The
   * chip holds its own state behind `plotChipRef`, so a hit costs the chip a
   * render and not this component. Off in the narrow shell, which has no hover.
   */
  const hoverBarRef = useRef<number | null>(null);
  const plotChipRef = useRef<((state: PlotChipState) => void) | null>(null);
  const onPlotHover = useCallback((hit: PlotHit | null) => {
    const index = hoverBarRef.current;
    plotChipRef.current?.(hit && index !== null ? { hit, index } : null);
  }, []);
  const narrow = useIsNarrow();
  const narrowRef = useRef(narrow);
  narrowRef.current = narrow;
  useEffect(() => {
    builderRef.current?.onHoverPlot(narrow ? null : onPlotHover);
    if (narrow) plotChipRef.current?.(null);
  }, [narrow, onPlotHover]);

  /**
   * The armed tools, for the rebuild.
   *
   * Their effects are keyed on the tool, and a rebuild does not change it — so
   * the new surface came up with the palette still lit and nothing armed on it.
   * Which is worse than it sounds: `setDrawingTool` is also what turns **pan
   * off**, so the chart the user then tried to draw on both ignored the tool and
   * scrolled under the pointer. Exactly the trap `chart-engine/CLAUDE.md`
   * describes for `priceFit`, and the same fix — hand it over at build time.
   */
  const drawingToolRef = useRef(drawingTool);
  drawingToolRef.current = drawingTool;
  const tradingToolRef = useRef(tradingTool);
  tradingToolRef.current = tradingTool;

  // Read inside the effect instead of closed over, so the effect can skip
  // height-only changes without going stale on everything else. The theme rides
  // the same trick: the build must start in the *current* theme without the
  // theme being a build dependency.
  const groupRef = useRef(group);
  groupRef.current = group;
  const themeRef = useRef(theme);
  themeRef.current = theme;
  const legendControlsRef = useRef(legendControls);
  /** Where the plot areas start — a left axis's width (`onPlotLeft`). */
  const [plotLeft, setPlotLeft] = useState(0);
  legendControlsRef.current = legendControls;

  /**
   * Export chart data… reads this chart's table through `lib/chart-export.ts`.
   *
   * A getter, asked only when somebody picks the command — the loaded columns
   * live in `dataRef` and the plot names in the builder's pane plans, and
   * copying either upwards on every load would move five thousand bars to serve
   * a command run once in a while. Named as the value legend names them:
   * `legendName` with the host's `nameOf`, so the file's headers are the words
   * on screen. Fills and Volume-by-Price are skipped for the legend's reasons
   * (no value of their own, no value at a bar), and a projection (`~fwd`)
   * because the export is bars that happened.
   */
  useEffect(
    () =>
      publishChartTable(group.id, () => {
        const data = dataRef.current;
        const builder = builderRef.current;
        if (!data || !builder) return undefined;
        const current = groupRef.current;
        const nameOf = legendControlsRef.current?.nameOf;
        const series: ExportSeries[] = [];
        for (const pane of current.panes) {
          for (const sp of builder.getPanePlan(pane.id)?.series ?? []) {
            if (sp.seriesKind === "band" || sp.plotType === "volumeByPrice") continue;
            if (sp.outputAlias?.endsWith("~fwd")) continue;
            const name = legendName(sp, current.symbol, nameOf?.(sp.plotId));
            if (sp.dataKind === "ohlc") {
              // The chart's own candles are the base columns; another symbol's
              // (`ohlc@SPY`) or a derived bar set are four columns of their own.
              if (legendColumn(sp, "close") === "close") continue;
              for (const field of ["open", "high", "low", "close"]) {
                const column = legendColumn(sp, field);
                if (column) series.push({ name: `${name} ${field}`, column });
              }
              continue;
            }
            const column = legendColumn(sp);
            if (column) series.push({ name, column });
          }
        }
        return {
          symbol: current.symbol,
          frequency: current.frequency,
          xKind: data.xKind,
          x: data.x,
          columns: data.columns,
          series,
        };
      }),
    [group.id],
  );

  // Derived from the group id rather than `useId()`: SciChart interpolates this
  // straight into `[id=…]` attribute selectors, which React's `:r3:` form breaks.
  // A ULID is already unique per chart and contains nothing a selector minds.
  const surfaceId = `qf-surface-${group.id}`;

  /**
   * Bumped when a refresh no longer meets the bars held (`onGap`) — the one
   * rebuild that is not caused by the group changing.
   *
   * Folded into `structure` rather than added to the rebuild effect's deps,
   * because every effect that re-applies a setting to the *new* builder is keyed
   * on `structure` too: a rebuild those did not see would come up with the
   * gridlines, margin and interactions reset under a menu still ticked. And not
   * a re-request: a bulk load onto a surface already drawn appends the whole
   * history on top of itself, so the surface has to go.
   */
  const [gapRebuilds, setGapRebuilds] = useState(0);
  const structure = `${structuralKey(group)}|gap:${gapRebuilds}`;
  const heightsKey = heightsKeyOf(group);
  // What was taken out of `structuralKey` has to come back as its own key, the
  // rule `axisRangesOf` and `stylesKey` already follow — the panes go into the
  // key sorted by id, so without this a reorder would reach nothing at all.
  const paneOrderKey = group.panes.map((pane) => pane.id).join("|");
  const visibleBars = visibleBarsOf(group);
  const rightMargin = rightMarginOf(group);
  // By value, not by object identity: every workspace action rebuilds the tree,
  // so an identity-keyed effect would re-apply every plot's style on every
  // keystroke in the symbol box.
  const candleDefault = useDefaultCandleStyle();
  const stylesKey = JSON.stringify(stylesOf(group, candleDefault));
  const hiddenKey = JSON.stringify(hiddenOf(group));
  const axisRangesKey = JSON.stringify(axisRangesOf(group));
  const interactionsKey = `${group.axisDrag ?? true}|${group.zoomAxis ?? "x"}`;

  /**
   * Put the X window where the anchor says, `bars` wide.
   *
   * The one place a window reaches the surface, so the four ways of asking for
   * one — a fresh load, the count box, a preset, the date box — cannot drift
   * apart. Reads through the refs rather than closing over props, so it is
   * stable enough to be called from inside the build effect's feed listeners.
   *
   * `bars` overrides the stored count for the caller that has just decided a new
   * one and has not seen it come back through props yet — a preset click is a
   * dispatch, and the render carrying its answer is a tick away.
   */
  /**
   * The bars on screen right now, or `undefined` before anything is drawn.
   *
   * Read off the axis rather than the workspace, because the wheel zooms
   * without writing `visibleBars` — so the two are only equal until somebody
   * scrolls. `pan` and `zoom` both start here.
   */
  const liveBars = useCallback((): number | undefined => {
    const builder = builderRef.current;
    const x = xRef.current;
    if (!builder || x.length === 0) return undefined;
    const range = builder.barWindow();
    if (!range) return undefined;
    const min = offsetAtTime(x, range.min);
    const max = offsetAtTime(x, range.max);
    if (min === undefined || max === undefined) return undefined;
    return barsInWindow({ min, max }, rightMarginOf(groupRef.current));
  }, []);

  const applyWindow = useCallback((anchor: Anchor, bars?: number) => {
    const builder = builderRef.current;
    const x = xRef.current;
    if (!builder || x.length === 0) return;
    const settings = groupRef.current;
    const visible = bars ?? visibleBarsOf(settings);
    // **The margin is the user's, and a projection does not widen it.**
    //
    // It briefly did: the margin grew to whatever it took to bring a forecast
    // on screen. That is the wrong default, and a twelve-month path is what
    // showed why — 260 projected sessions against a 150-bar window squeezed the
    // candles into the left third of the chart. Adding an overlay must not
    // resize the thing it is drawn over.
    //
    // So the right edge stays where `rightMargin` puts it: past the last
    // **bar**, by a share of the window. A projection reaching beyond it is
    // simply off-screen until the reader zooms out or asks for more margin —
    // which is the same bargain every other overlay makes with the window.
    const margin = rightMarginOf(settings);
    // The last *bar*, not the last row. Anchoring "end" on the array would open
    // every chart carrying a projection on the forecast, with the price off the
    // left edge — the data is all there and none of it is what you asked for.
    const lastBar = Math.max(0, realBarsRef.current - 1);

    const next = (() => {
      if (anchor.at === "bar") return windowAround(anchor.index, realBarsRef.current, visible, margin);
      // Read back in **bar offsets**, because that is what the arithmetic below
      // is in — a count of bars and a margin expressed as a share of it. The
      // axis answers in time, so it converts on the way in and back out again;
      // `lib/chart-window.ts` never learns the axis changed.
      const range = anchor.at === "hold" || anchor.at === "pan" ? builder.barWindow() : undefined;
      const current =
        range &&
        (() => {
          const min = offsetAtTime(x, range.min);
          const max = offsetAtTime(x, range.max);
          return min === undefined || max === undefined ? undefined : { min, max };
        })();
      // No window yet (the very first apply) is the same as "end": there is
      // nothing to hold on to, and holding a SciChart default range would put
      // the chart somewhere nobody asked for.
      if (!current) return windowEndingAt(lastBar, visible, margin);
      if (anchor.at !== "pan") {
        return windowHoldingRight(current, appliedGapRef.current, visible, margin);
      }
      // **Panning keeps the width that is on screen, not the stored count.**
      // The wheel changes the X range without writing `visibleBars` — it is a
      // `MouseWheelZoomModifier` on the axis — so a press that rebuilt the
      // window from the stored count undid the wheel zoom, which is the one
      // thing `windowPannedBy` promises not to do. Measured back out of the
      // live range, and the step is a quarter of *that*.
      const live = barsInWindow(current, margin);
      return windowPannedBy({
        current,
        appliedGap: live * margin,
        visibleBars: live,
        rightMargin: margin,
        deltaBars: anchor.direction * panStep(live),
        barCount: realBarsRef.current,
      });
    })();

    // Recorded because the *next* hold has to subtract it to find the last bar,
    // and by then the margin that produced it may already have changed — which
    // is exactly the case that makes turning a right margin on add bars on the
    // left instead of space on the right.
    appliedGapRef.current = rightGap(visible, margin);
    const min = timeAtOffset(x, next.min);
    const max = timeAtOffset(x, next.max);
    // Both or neither: a range with one end resolved is a window nobody asked
    // for, and the axis would fit it as readily as a correct one.
    if (min !== undefined && max !== undefined) builder.setBarWindow({ min, max });
  }, []);


  /**
   * Whether a history preview is running, and the frame loop that drives it.
   *
   * The animation is a `visibleBars` count per frame handed to `applyWindow`
   * anchored at the **end** — so the right edge is pinned to the last bar and
   * only the left one travels, which is what makes it read as history arriving
   * rather than as the chart panning.
   *
   * Held in a ref as well as in state: the ref is what `cancel` and the effect
   * teardown need, and state is only there to light the button. A running
   * animation that survived unmount would call `setBarWindow` on a disposed
   * surface, which is a WebGL error and not a React one.
   */
  const priceBottom = useMemo(() => priceBottomFraction(group.panes), [group.panes]);
  // The band's own top, for anything pinned to it: the price pane is not
  // always pane 0 — the reorder strip can put volume above it.
  const priceTop = useMemo(() => priceTopFraction(group.panes), [group.panes]);

  /**
   * The PIP's bars, and they are **deliberately not `xRef`**.
   *
   * The panel is a second chart group on the same socket, so its frames arrive
   * through the same three listeners. Letting them reach the chart's own state
   * is the failure this whole seam exists to prevent: weekly bars appended to
   * the daily series, a bar count that counts both, a window applied to the
   * wrong one — every symptom of which renders perfectly.
   *
   * A ref plus a counter rather than state holding the array, for `xRef`'s
   * reason: a realtime frame revises the last bar several times a minute and
   * only the panel needs to hear about it.
   */
  const pipBarsRef = useRef<PipBar[]>([]);
  const [pipStamp, setPipStamp] = useState(0);

  /**
   * The closes inside the **visible window**, which is what the placement rule
   * counts.
   *
   * Not every loaded close: `barsRange` is 1500 against a 150-bar window by
   * default, so counting all of them would answer for nine screens the reader
   * cannot see. The axis knows the window in *time*, and `offsetAtTime` is the
   * seam back to the array — the same conversion `applyWindow` makes going the
   * other way.
   *
   * **It deliberately does not chase a pan.** Panning re-renders nothing, and
   * making it do so would recompute the corner sixty times a second and send the
   * panel hopping across the chart mid-drag. It settles on the next thing that
   * genuinely changed — bars arriving, the panel resizing, the window being
   * applied — which is when a reader looks at it anyway.
   */
  const pipCloses = useMemo(() => {
    const closes = dataRef.current?.columns["close"];
    if (!closes?.length) return [] as number[];
    const range = builderRef.current?.barWindow();
    const x = xRef.current;
    if (!range || x.length === 0) return closes;
    const from = offsetAtTime(x, range.min);
    const to = offsetAtTime(x, range.max);
    if (from === undefined || to === undefined) return closes;
    return closes.slice(Math.max(0, Math.floor(from)), Math.min(closes.length, Math.ceil(to) + 1));
  }, [barStamp, dataStamp, pipStamp, group.visibleBars, group.pipSize, group.pipView]);

  const [previewing, setPreviewing] = useState(false);
  const previewRef = useRef<number | null>(null);

  const stopPreview = useCallback(() => {
    if (previewRef.current !== null) cancelAnimationFrame(previewRef.current);
    previewRef.current = null;
    setPreviewing(false);
  }, []);

  const runPreview = useCallback(() => {
    const from = visibleBarsOf(groupRef.current);
    const to = previewTarget(xRef.current, from, DEFAULT_SPAN_YEARS);
    if (to === undefined) return;

    stopPreview();
    setPreviewing(true);
    const started = performance.now();

    const step = () => {
      const bars = previewFrame(from, to, performance.now() - started, DEFAULT_TIMINGS);
      if (bars === null) {
        // Restored explicitly rather than trusting the last frame: the loop can
        // be cut short by a resize or a rebuild, and "ends where it started" has
        // to hold on the way out of every one of them.
        applyWindow({ at: "end" }, from);
        previewRef.current = null;
        setPreviewing(false);
        return;
      }
      applyWindow({ at: "end" }, bars);
      previewRef.current = requestAnimationFrame(step);
    };
    previewRef.current = requestAnimationFrame(step);
  }, [applyWindow, stopPreview]);

  // Cancelling on unmount is not tidiness: `step` calls into the builder, and a
  // frame that lands after teardown reaches a disposed WebGL surface.
  useEffect(() => stopPreview, [stopPreview]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let disposed = false;
    /** Whether the error on screen is the socket's, which a reopen ends. */
    let socketError = false;
    setError(null);
    const builder = new SciChartJsChartBuilder({
      theme: themeRef.current,
      // The reference lines an indicator declares — RSI's 30/70, MACD's zero.
      // A live lookup, not a snapshot: the surface is built as soon as the
      // container exists and the catalog arrives over HTTP afterwards, so a
      // value captured here would be empty forever. The `refreshReferenceLines`
      // below is the other half.
      axisSpecs: cachedAxisSpecs(),
      // JAS Timing's 0 is "down", not "did not fire" — see `cachedEventKinds`.
      eventKinds: cachedEventKinds(),
      // The exchange's clock on an intraday chart, dates on everything else.
      // The interval is structural, so this is fixed for the builder's life.
      timeLabels: timeLabelsFor(groupRef.current.symbol, groupRef.current.frequency),
    });
    builderRef.current = builder;
    // A left axis moves where the plot starts; the legend follows it.
    builder.onPlotLeft = setPlotLeft;
    debugBuilder(groupRef.current.id, builder);

    // **Older bars before the reader reaches the first one** — intraday only;
    // a daily chart is sent its whole file (`qv.complete`). Raising
    // `barsRange` refetches through the ordinary structural rebuild, exactly as
    // a preset does, and `pendingCenterRef` carries the window across it so the
    // chart lands where it was rather than back at today. The server computes
    // every indicator over a warm-up before the first bar it sends, so the
    // lines the reader was looking at do not move when the older bars arrive.
    let limitSaid = false;
    builder.onBarWindowChanged((window) => {
      if (disposed) return;
      const link = windowLinkRef.current;
      if (link) publishWindow(link, groupRef.current.id, window);
      if (loadingMoreRef.current) return;
      const g = groupRef.current;
      if (!isIntraday(g.frequency)) return;
      const plan = planPanLoad({
        x: xRef.current,
        windowMin: window.min,
        visibleBars: visibleBarsOf(g),
        barsRange: g.barsRange,
        complete: completeRef.current,
      });
      if (!plan) return;
      if ("atLimit" in plan) {
        if (!limitSaid) {
          limitSaid = true;
          setNotice(`${MAX_INTRADAY_BARS.toLocaleString("en-US")} bars is the most an intraday chart loads`);
        }
        return;
      }
      loadingMoreRef.current = true;
      pendingCenterRef.current = (window.min + window.max) / 2;
      raiseBars(plan.barsRange);
    });

    // Inside the build effect rather than in one of its own, because a rebuild
    // constructs a *new* builder: a subscription made elsewhere would be keyed
    // on a value that has not changed, so it would never re-fire, and the legend
    // would freeze on the bar it last read after any symbol or interval change.
    builder.onHoverPlot(narrowRef.current ? null : onPlotHover);
    builder.onHoverTime((time) => {
      // **The chart answers in time; the columns are read by position.** The
      // conversion lives here because this is where the bar times live —
      // `xRef.current` is the array the load put there, and asking the renderer
      // to hand back what it was given would be the longer way round.
      //
      // Off the end is `null`, not a clamp: there is margin to the right of
      // every chart, so the pointer sits past the last bar constantly, and the
      // nearest bar is a reading nobody pointed at. `indexAtTime` already
      // answers `undefined` outside the loaded range rather than clamping.
      // The crosshair link gets the **bar's** date — the one the snapped line
      // stands on — so a peer places the same day whatever fraction of it the
      // pointer is over. Off the bars it is `null`, and the peers hide.
      const bar = time !== null ? indexAtTime(xRef.current, time) : undefined;
      hoverBarRef.current = bar ?? null;
      const link = crosshairLinkRef.current;
      if (link) {
        publishCrosshair(link, groupRef.current.id, bar !== undefined ? xRef.current[bar]! : null);
      }
      const at = legendOnRef.current && bar !== undefined ? bar : null;
      if (hoverRef.current === at) return;
      hoverRef.current = at;
      setHoverIndex(at);
    });

    // Fire-and-forget: the chart is fully usable without it, and a host with no
    // catalog reachable gets no reference lines rather than a blocked build.
    void loadIndicatorCatalog().then(() => {
      // The twins cannot be resolved until the catalog says which families are
      // projectable, so a load has to re-run the fold above.
      setCatalogSize(cachedCatalog().length);
      if (!disposed) builder.refreshReferenceLines();
    });

    /**
     * Whether this frame belongs to the PIP, running `apply` if it does.
     *
     * Returns a boolean rather than taking both branches, so each listener reads
     * as "the PIP's, or ours" — and an id belonging to *neither* falls through
     * to the chart, which is the pre-existing behaviour and the only safe
     * default: the fixture transport does not echo our ids at all.
     */
    const pipId = String(pipChartGroupIdInt(groupRef.current.id));
    const routePip = (id: string | undefined, apply: () => void): boolean => {
      if (id !== pipId) return false;
      apply();
      return true;
    };

    // Same listeners drive both transports; the chart-feed mapping is identical.
    const listeners: TransportListeners = {
      // Every frame of an intraday chart, new bar or not — the dot must not go
      // grey on a quiet minute. The PIP's frames describe the PIP, not this.
      onLive: (info) => {
        if (disposed || info.chartGroupId === pipId) return;
        setLiveInfo(info);
      },
      // The PIP draws candles only, so its shapes (it asks for none) are never ours.
      onGeometry: (frame) => {
        if (disposed || frame.chartGroupId === pipId) return;
        setGeometry(frame.geometry);
      },
      onBulkLoad: (load) => {
        if (disposed) return;
        // **Route first, and route on the id the server echoed.** Two chart
        // groups ride this socket whenever the PIP is showing a chart, and
        // before this line every frame was applied to the main surface — which
        // appends a weekly series onto the daily one and draws it without
        // complaint. The PIP's frames stop here.
        if (routePip(load.columns.chartGroupId, () => {
          pipBarsRef.current = pipBars(load.columns, groupRef.current.barSource);
          setPipStamp((n) => n + 1);
        })) {
          return;
        }
        // **Map and bind are timed apart, and the split is the finding.**
        // `bulkDeltas` re-keys columns onto plots; `updateSeries` appends into
        // SciChart a point at a time (`appendData`, not `appendRange`). They
        // read as one step and are not one cost, and only one of them is ours
        // to fix.
        const mapStarted = perfNow();
        const deltas = bulkDeltas(load.columns, groupRef.current);
        const bindStarted = perfNow();
        for (const d of deltas) builder.updateSeries(d);
        const bindEnded = perfNow();

        // The bar timestamps, kept because index space is all a category axis
        // knows: "go to March" is a search through these and nothing else can do
        // it. Copied rather than aliased — the decoded columns belong to the
        // transport and the realtime path appends to this one.
        xRef.current = [...load.columns.x];
        // Both counts, and the whole array. The projection's dates belong on
        // the axis — that is how a cone is placed and hovered — while the
        // controls below all mean the bars.
        realBarsRef.current = realBarCount(load.columns);
        completeRef.current = load.columns.complete === true;
        loadingMoreRef.current = false;
        labelsRef.current = load.columns.labels ?? {};
        setBarsLoaded(realBarsRef.current);
        // The annotations are placed against these, so they have to arrive
        // before anything is drawn — a wider load moves every bar's *index*
        // while leaving its *time* alone, and a drawing placed against the old
        // indices would slide by the difference.
        const barTimesStarted = perfNow();
        builder.setBarTimes(xRef.current, realBarsRef.current);
        const barTimesEnded = perfNow();
        setBarStamp((n) => n + 1);

        // The last close, upwards, so "new plan" can open pre-filled. Published
        // rather than held, because the price belongs to the chart that loaded
        // it and the plan form is mounted three levels above.
        // The last **real** close. Past the settled edge every OHLCV column is
        // null, so reading the array's end would publish `NaN` as the price a
        // new trading plan opens on.
        const closes = load.columns.columns["close"];
        const last = closes?.[realBarsRef.current - 1];
        // The last bar's *time* travels with its price. A plan created from the
        // form has no drag to take a start from, and `createdAt` is a moment
        // during the session while the bars are stamped midnight — so without
        // this its levels start past the end of the data and span the chart
        // instead of raying from it.
        const at = xRef.current[realBarsRef.current - 1];
        if (typeof last === "number" && Number.isFinite(last) && at !== undefined) {
          lastPriceRef.current?.(groupRef.current.symbol, last, at);
        }

        // What the server actually sent, kept beside what was asked for. The
        // request is rebuilt rather than remembered because `toIndicatorRequest`
        // is the one place that decides which aliases go on the wire — a second
        // list here would be a second answer to the same question.
        const snapshotStarted = perfNow();
        dataRef.current = {
          chartGroupId: load.columns.chartGroupId,
          status: load.columns.status,
          error: load.columns.error,
          symbol: load.columns.symbol,
          xKind: load.columns.xKind,
          x: [...load.columns.x],
          columns: Object.fromEntries(
            Object.entries(load.columns.columns).map(([name, values]) => [name, [...values]]),
          ),
          requested: toIndicatorRequest(groupRef.current, layoutId, "demo")
            .chartGroups[0]!.indicators.flatMap((ind) => ind.aliases ?? []),
          // Carried across from whatever the previous frame left, so the open
          // survives a refresh landing on top of it.
          timings: dataRef.current?.timings ?? {},
        };
        const snapshotEnded = perfNow();

        // **Both records, and the open is the one that cannot be recovered.**
        // After sixty seconds the first load is gone and only refreshes remain,
        // so the cost a reader actually felt opening the chart would never be
        // visible again. Written last because it measures the copy above.
        const record: ChartTimingRecord = {
          at: Date.now(),
          kind: "open",
          server: load.columns.serverPerf,
          client: {
            wireMs: load.timing?.wireMs ?? 0,
            decodeMs: load.timing?.decodeMs ?? 0,
            diffMs: load.timing?.diffMs ?? 0,
            mapMs: bindStarted - mapStarted,
            bindMs: bindEnded - bindStarted,
            barTimesMs: barTimesEnded - barTimesStarted,
            snapshotMs: snapshotEnded - snapshotStarted,
            bytes: load.timing?.bytes ?? 0,
            groupCount: load.timing?.groupCount ?? 1,
          },
        };
        dataRef.current.timings = { open: record, latest: record };
        setDataStamp((n) => n + 1);

        // **The window is applied here, and here is why it has to be.** A load
        // is the only moment the chart has both a surface and data; a preset
        // that raised `barsRange` fetched correctly and then showed the default
        // 150 bars until this line existed. A date asked for beyond the old
        // history is honoured on the same beat, or the refetch it triggered
        // lands somewhere else entirely.
        const wanted = pendingCenterRef.current;
        pendingCenterRef.current = undefined;
        const x = xRef.current;
        if (wanted !== undefined && x.length > 0 && wanted >= x[0]!) {
          applyWindow({ at: "bar", index: Math.min(indexAtOrAfter(x, wanted), x.length - 1) });
        } else {
          applyWindow({ at: "end" });
        }
      },
      onAppend: (frame) => {
        if (disposed) return;
        if (routePip(frame.chartGroupId, () => {
          pipBarsRef.current = applyPipDeltas(
            pipBarsRef.current,
            frame.deltas,
            groupRef.current.barSource,
          );
          setPipStamp((n) => n + 1);
        })) {
          return;
        }
        const mapStarted = perfNow();
        const mapped = appendDeltas(frame.deltas, groupRef.current);
        const bindStarted = perfNow();
        for (const d of mapped) builder.updateSeries(d);
        const bindEnded = perfNow();
        let barTimesMs = 0;
        // Kept in step so a date lookup after an hour of streaming still lands
        // on the right bar. Deliberately without moving the window: a new bar
        // must not drag someone reading 2021 back to today.
        const appended = frame.deltas.find((d) => d.append)?.append?.x;
        if (appended?.length) {
          // **Spliced in at the settled edge, not pushed onto the end.** With a
          // projection loaded the array's tail is a set of future dates, and
          // tomorrow's bar belongs *before* them — appending would leave `xRef`
          // no longer ascending, which silently breaks the binary search that
          // answers "which bar is the crosshair on" and "go to this date".
          //
          // The new bar also lands on a date the projection already holds — a
          // cone anchored on the last candle draws its next point on the next
          // session — so the covered dates are dropped rather than duplicated.
          // The times on the axis are unchanged either way; what changes is
          // which side of the edge they sit on.
          const real = realBarsRef.current;
          const newest = appended[appended.length - 1]!;
          const ahead = xRef.current.slice(real).filter((t) => t > newest);
          xRef.current = [...xRef.current.slice(0, real), ...appended, ...ahead];
          realBarsRef.current = real + appended.length;
          setBarsLoaded(realBarsRef.current);
          // A new bar extends the range a drawing can be placed in, so one
          // pinned past the last bar becomes drawable. Cheap: the builder
          // re-places what it already holds rather than rebuilding anything.
          const barTimesStarted = perfNow();
          builder.setBarTimes(xRef.current, realBarsRef.current);
          barTimesMs = perfNow() - barTimesStarted;
        }

        // The inspector's copy, kept in step the same way — mutated in place,
        // and re-rendered only when someone is looking at it.
        const snap = dataRef.current;
        if (snap) {
          const snapshotStarted = perfNow();
          if (appended?.length) for (const t of appended) snap.x.push(t);
          for (const d of frame.deltas) {
            const col = snap.columns[d.alias];
            if (!col) continue;
            if (d.append?.y) for (const v of d.append.y) col.push(v);
            if (d.patchLast?.y !== undefined && col.length > 0) col[col.length - 1] = d.patchLast.y;
          }
          const snapshotMs = perfNow() - snapshotStarted;

          // **`latest` moves, `open` does not.** They answer different
          // questions — what opening this chart cost, and what it costs to keep
          // it current — and the 60s refresh would otherwise erase the first
          // within a minute of every load.
          //
          // Recorded whether or not anyone is looking: the panel is opened
          // *after* something felt slow, so a reading collected only while it is
          // already open is a reading of the wrong frame. It is one small
          // object per refresh and it replaces its predecessor.
          snap.timings = {
            ...snap.timings,
            latest: {
              at: Date.now(),
              kind: "update",
              server: frame.serverPerf,
              client: {
                wireMs: frame.timing?.wireMs ?? 0,
                decodeMs: frame.timing?.decodeMs ?? 0,
                diffMs: frame.timing?.diffMs ?? 0,
                mapMs: bindStarted - mapStarted,
                bindMs: bindEnded - bindStarted,
                barTimesMs,
                snapshotMs,
                bytes: frame.timing?.bytes ?? 0,
                groupCount: frame.timing?.groupCount ?? 1,
              },
            },
          };
          if (inspectingRef.current) setDataStamp((n) => n + 1);
        }
        if (barIntelPlot(groupRef.current.panes.find((p) => p.role === "price")?.plots)) {
          setTickStamp((n) => n + 1);
        }
      },
      onError: (e) => {
        if (disposed) return;
        socketError = e.source === "socket";
        setError(e.message);
      },
      // Bars went missing between what this surface holds and the refresh that
      // just arrived. Nothing was appended; rebuild, and the new surface's
      // request comes back as a whole load (the hub asks for it by `reload`).
      onGap: () => {
        if (disposed) return;
        setGapRebuilds((n) => n + 1);
      },
      onStatus: (status) => {
        // Guarded like the others: the teardown below disconnects, and a
        // "closed" from the client we are throwing away must not paint a badge
        // on the surface that replaced it.
        if (disposed) return;
        setFeed(status);
        // A socket's error ends when the socket is back. Nothing else cleared
        // it, so "chart error: WebSocket error" stood in red over a chart that
        // had reconnected and was ticking again — and read as a reason to
        // reload. Only the socket's own: a build failure is not fixed by one.
        if (status === "open" && socketError) {
          socketError = false;
          setError(null);
        }
      },
    };

    // **One socket and one request for the whole layout, not one per pane.**
    //
    // This used to build its own client here. Two panes were then two sockets
    // and two requests, and the server read the same symbol file once per
    // request — which is the cost that matters: measured on real AAPL bars,
    // four groups on one symbol are 31.6 ms with separate reads and 11.2 ms
    // sharing one, because `base_frame` is 7.6 ms warm and everything above it
    // is about a millisecond.
    //
    // `lib/chart-hub.ts` owns the client, merges every mounted group into one
    // request, and hands each frame back to whoever owns that `chartGroupId`.
    // The listeners above are unchanged and still route internally — `routePip`
    // still separates this chart's frames from its PIP's, because the hub
    // delivers both to this member.
    const requested = toIndicatorRequest(groupRef.current, layoutId, "demo");
    const feed = joinChartHub({
      // Distinguishes sockets. The fixture has no URL and must not share a hub
      // with a live one, so it names itself.
      socketKey: QV_WS_URL || "fixture",
      layoutId,
      userId: "demo",
      // One member per mounted chart group. Re-registering replaces rather than
      // adding, which is what makes this safe to call from an effect that
      // re-runs on a symbol change.
      memberKey: String(groupRef.current.id),
      member: {
        // Both ids: the chart's and its PIP's, when the panel is a chart. The
        // hub needs them to route, and listing them here keeps it ignorant of
        // how a PIP id is formed.
        ids: requested.chartGroups.map((cg) => Number(cg.chartGroupId)),
        groups: requested.chartGroups,
        listeners,
      },
      factory: (hubListeners) =>
        QV_WS_URL
          ? new QvWebSocketClient({
              url: QV_WS_URL,
              listeners: hubListeners,
              authToken: idToken,
              pass: currentPass,
              actAs: actingAs,
            })
          : new FixtureTransportClient({
              bars: groupRef.current.barsRange ?? 160,
              streamFrames: 20,
              intervalMs: 700,
              listeners: hubListeners,
            }),
    });

    void (async () => {
      // One parent surface, one SubChart per pane (shared WASM context).
      await builder.buildGroup(
        // The viewer's default candle style, applied on the way in and never
        // written back — see `lib/candle-style.ts`.
        withCandleDefault(groupRef.current.panes, defaultCandleStyle()),
        container,
        groupRef.current.gridLines,
        resolveInteractions(groupRef.current),
        // Read from the ref at build time, like the two above: this survives a
        // rebuild only by being handed over here. Its own effect is keyed on the
        // value, so a symbol change — which rebuilds — would otherwise leave the
        // new surface fitting every plot while the menu still ticked Price only.
        groupRef.current.priceFit ?? "price",
        // Same reason again, and the sharpest case of it: these come from a
        // fetch rather than from the model, so nothing in `panes` would put them
        // back. Read through a ref because this effect is keyed on `structure`
        // and must not re-run every time a quarter arrives.
        markersRef.current,
      );
      if (disposed) return;
      // Re-armed before the data arrives, not after: the palette is already lit
      // and the pointer is already over the chart, so any gap here is a drag
      // that pans instead of drawing.
      builder.setDrawingTool(drawingToolRef.current);
      builder.setTradingTool(tradingToolRef.current ?? null);
      // A new surface has none — and the effect that sets them is keyed on the
      // value, which a rebuild does not change.
      builder.setEstimateLines(estimatesRef.current);
      // A new builder starts on the fit — manual Y is never carried over a
      // rebuild (`lib/y-axis-mode.ts`) — and reports a drag from here on.
      publishYManual(groupRef.current.id, false);
      builder.setYManualHandler((manual) => publishYManual(groupRef.current.id, manual));
      // No `connect` or `request` here any more: the hub does both, once, for
      // every group that registered in this commit. Joining above is what
      // schedules it.

      // Keyed by group as well as last-one-wins: with several charts mounted a
      // single handle names whichever finished building last, which is the one
      // question a two-chart bug is never asking. Both, because `__mmBuilder` is
      // what a console session reaches for on a one-chart layout.
      // **Publish a read-only snapshot source, not the builder.**
      //
      // The Summary tab describes the chart the reader is looking at, and the
      // three things it needs — the interval, the plotted series' latest values,
      // and a picture — all live in this component. It is not a descendant of
      // this one and sometimes not even in this window, so the closure goes into
      // a registry keyed by group id rather than being threaded back up through
      // four components. `lib/chart-snapshot.ts` carries the argument.
      //
      // Inside the build effect on purpose: the entry is torn down with the
      // surface, so a snapshot can never be taken against a disposed builder.
      unregisterImage.current?.();
      // The picture alone, for a community post — `lib/chart-snapshot.ts`.
      unregisterImage.current = registerChartImage(groupRef.current.id, async () =>
        (await builderRef.current?.captureImage()) ?? null,
      );
      unregisterSnapshot.current?.();
      unregisterSnapshot.current = registerChartSnapshot(
        groupRef.current.id,
        async (): Promise<ChartSnapshot> => {
          const snapshot = dataRef.current;
          // Not `window` — that shadows the global one this file also uses.
          const bars = builderRef.current?.barWindow();
          const latest: Record<string, number> = {};
          for (const [name, values] of Object.entries(snapshot?.columns ?? {})) {
            const last = values.at(-1);
            if (typeof last === "number" && Number.isFinite(last)) latest[name] = last;
          }

          // **The closes on screen, thinned to a shape the model can read.**
          //
          // Sliced to the visible window rather than the loaded range: a chart
          // holds 1500 bars and shows 150, and describing the 1350 nobody can
          // see would answer a question that was not asked.
          const closes = snapshot?.columns?.close ?? [];
          const from = Math.max(0, Math.floor(bars?.min ?? 0));
          const to = Math.min(closes.length, Math.ceil(bars?.max ?? closes.length));
          const visible = closes.slice(from, to).filter((v) => Number.isFinite(v));
          const step = Math.max(1, Math.ceil(visible.length / CLOSE_PATH_POINTS));
          const closePath = visible
            .filter((_, index) => index % step === 0 || index === visible.length - 1)
            .map((value) => Number(value.toPrecision(6)));
          // The aliases *asked for*, minus the base price columns — which is
          // what the reader thinks of as "the indicators on my chart".
          const indicators = (snapshot?.requested ?? []).filter(
            (alias) => !SNAPSHOT_BASE_COLUMNS.has(alias),
          );
          // **No picture is sent.** `captureImage` still exists on the builder
          // and the endpoint still accepts `imagePng`; this simply stops paying
          // for one. Measured on MRVL, same 150 bars, one note each way: the two
          // technical readings were the same reading — trend against the 50-day,
          // the shape of the advance and the pullback, position between the
          // window's high and low — and neither cited anything only a picture
          // could give: no pattern, no structural level, no gap. With no image
          // the note did not even list the chart under `missing`.
          //
          // It cost 1,078 input tokens and up to two seconds of waiting for a
          // frame, to paraphrase `closePath`.
          const first = visible[0];
          const last = visible.at(-1);
          return {
            interval: intervalLabel(groupRef.current.frequency),
            barsShown: bars ? Math.max(0, Math.round(bars.max - bars.min)) : 0,
            indicators,
            latest,
            closePath,
            ...(visible.length
              ? {
                  windowLow: Number(Math.min(...visible).toPrecision(6)),
                  windowHigh: Number(Math.max(...visible).toPrecision(6)),
                }
              : {}),
            ...(first && last && first !== 0
              ? { windowChange: Number((last / first - 1).toFixed(4)) }
              : {}),
          };
        },
      );

      if (import.meta.env.DEV) {
        const w = window as unknown as {
          __mmBuilder?: unknown;
          __mmBuilders?: Record<string, unknown>;
        };
        w.__mmBuilder = builder;
        w.__mmBuilders = { ...(w.__mmBuilders ?? {}), [groupRef.current.id]: builder };
      }
    })().catch((err: unknown) => {
      // A disposed effect closed its own socket, so an in-flight request failing
      // is the teardown working rather than a fault. React's StrictMode double
      // mount makes that the *normal* path on every dev page load — connect,
      // request, unmount, disconnect — and it logged an error every time. A
      // console line that is always there is a console line nobody reads, which
      // costs the one that matters.
      if (disposed) return;
      const msg = err instanceof Error ? err.message : String(err);
      // eslint-disable-next-line no-console
      console.error("[chart] build/feed failed:", err);
      socketError = false;
      setError(msg);
    });

    return () => {
      disposed = true;
      builderRef.current = null;
      debugBuilder(groupRef.current.id, null);
      unregisterSnapshot.current?.();
      unregisterSnapshot.current = null;
      unregisterImage.current?.();
      unregisterImage.current = null;
      // Dropped with the surface they describe. A rebuild is a new symbol, a new
      // interval or a bigger load, so keeping the old timestamps would answer
      // "go to March" against the chart that is being thrown away — and it would
      // answer plausibly, which is the worst kind of wrong.
      xRef.current = [];
      realBarsRef.current = 0;
      completeRef.current = false;
      loadingMoreRef.current = false;
      builder.onBarWindowChanged(null);
      setBarsLoaded(0);
      // A new surface says nothing about being live until its first frame does.
      setLiveInfo(null);
      // Nor about lines: the old symbol's would sit at the same dates.
      setGeometry(null);
      // Dropped with the surface for the same reason: a rebuild is a new symbol,
      // a new interval or a bigger load, and an inspector showing the previous
      // chart's columns would answer plausibly about the wrong data.
      dataRef.current = null;
      // Leaves the shared socket rather than closing it: the last member out
      // disconnects, and while others remain it re-sends so the server stops
      // computing a group nobody is listening for.
      feed.leave();
      builder.dispose();
    };
  }, [structure, layoutId, applyWindow]);

  /**
   * **Showing that the chart is live** (owner-approved mockup, 2026-09-29):
   * the dot top-right, the forming bar's countdown on the price label, and a
   * pale yellow band behind the forming bar — yellow being this app's "not
   * final", the same token the projections use.
   *
   * One second's tick while the market is live; nothing runs when it is not,
   * and both chart decorations are taken off. The band and the label are
   * property writes on annotations that already exist, so a tick is a repaint
   * of two things, never a rebuild.
   */
  // Daily and up too (2026-09-29): the server stamps `qv.today`/`qv.phase` on a
  // daily frame when today's bar is spliced, and says nothing otherwise (no
  // freshness, India, a ratio), which `liveState` reads as no dot. The band and
  // the countdown stay intraday: `periodSeconds` is undefined for 1D. The band
  // came to 1D+ on 2026-10-13; the countdown did not.
  const live: LiveState | null = liveState(liveInfo, Date.now());
  const ticking = live === "live" || live === "delayed";
  useEffect(() => {
    if (!ticking) return;
    const id = window.setInterval(() => setLiveTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [ticking]);
  // For the toolbar's badge, which `App` renders in another subtree.
  useEffect(() => {
    publishLive(group.id, live ? { state: live, tick: liveTick } : null);
  }, [group.id, live, liveTick]);
  useEffect(() => () => publishLive(group.id, null), [group.id]);
  // The last pre- or post-market print, as a yellow label on the price axis
  // (owner, 2026-09-29). Every frame restates it, so a frame without it — the
  // session, or nothing printed — takes it off.
  const extPrice = liveInfo?.extPrice ?? null;
  const extPhase = liveInfo?.extPhase ?? "post";
  useEffect(() => {
    builderRef.current?.setExtendedPrice(extPrice, extPhase);
  }, [extPrice, extPhase, structure]);
  const pricePlotId = pricePlot(group)?.id;
  useEffect(() => {
    const builder = builderRef.current;
    if (!builder) return;
    const period = periodSeconds(group.frequency);
    const last = xRef.current[realBarsRef.current - 1];
    // **Not once the bar is over.** After the close no next bar comes (the
    // chart is regular hours), so the countdown sat at `00:00` all evening
    // (owner, 2026-09-29). A minute's grace covers the push that brings the
    // next bar during the session.
    // **Daily and up while the dot is on** (owner, 2026-10-13): the server
    // stamps `qv.today` only when it spliced today's bar, so a ticking 1D chart's
    // last bar is the one still forming. No countdown — a day ends at the close,
    // not at the bar's nominal end. Its slot is measured from its neighbour,
    // because `last ± 12 h` is lopsided on this axis.
    if (ticking && period === undefined && last !== undefined) {
      builder.setFormingBar(barSlot(xRef.current.slice(0, realBarsRef.current), last) ?? null);
      if (pricePlotId) builder.setLastLabelSuffix(pricePlotId, null);
      return;
    }
    if (!ticking || period === undefined || last === undefined || barOver(last, period, Date.now() / 1000)) {
      builder.setFormingBar(null);
      if (pricePlotId) builder.setLastLabelSuffix(pricePlotId, null);
      return;
    }
    builder.setFormingBar(formingBand(last, period));
    if (pricePlotId) builder.setLastLabelSuffix(pricePlotId, countdown(last, period, Date.now() / 1000));
  }, [ticking, liveTick, barsLoaded, pricePlotId, group.frequency, structure]);

  /**
   * **Inside / Outside Bars**: a pale column behind each, classified from the
   * bars the chart holds (`lib/inside-outside.ts`). The bar still forming is
   * left out — its range moves — so a live chart settles one bar short.
   */
  useEffect(() => {
    const builder = builderRef.current;
    if (!builder) return;
    const bars = chartBars(dataRef.current, realBarsRef.current);
    const period = periodSeconds(group.frequency);
    const last = bars.t[bars.t.length - 1];
    const forming =
      ticking && last !== undefined && (period === undefined || !barOver(last, period, Date.now() / 1000));
    const settled = bars.t.length - (forming ? 1 : 0);
    barKindsRef.current = insideOutsideKinds(group, bars, settled);
    builder.setBarHighlights(insideOutsideHighlights(group, bars, settled), INSIDE_OUTSIDE_LAYER);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.panes, group.frequency, barStamp, tickStamp, ticking, structure]);

  /**
   * **Seasonality bands**: the server's `span` shapes as backgrounds behind the
   * candles (`lib/seas-bands.ts`). Against the full x: an upcoming window lies
   * on the empty future rows the server added (`forward_bars`).
   */
  useEffect(() => {
    const builder = builderRef.current;
    if (!builder) return;
    const times = xRef.current;
    const price = group.panes.find((pane) => pane.role === "price");
    builder.setSpans(seasBandSpans(geometry, price?.plots, times), SEAS_BANDS_LAYER);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group.panes, geometry, barStamp, structure]);

  /**
   * Ask SciChart how the licence went, once a surface exists to make it answer.
   *
   * **Declared after the build effect on purpose.** Effects run in declaration
   * order, which is the same guarantee the pane setters below already rely on —
   * and here it is what makes the question answerable at all, since the licence
   * flow only runs when there is a surface to run it over.
   */
  useEffect(() => {
    let live = true;
    void chartsLicensed().then((verdict) => {
      if (live) setLicence(verdict);
    });
    return () => {
      live = false;
    };
  }, []);

  /**
   * Re-stack the panes without rebuilding.
   *
   * **Declared before the heights effect, and that ordering is load bearing.**
   * `setPaneHeights` is positional against the builder's pane Map, so applying
   * heights while that Map is still in the old order writes each height onto
   * whichever pane took its slot. Moving the price pane down a chart with a
   * volume pane put `1fr` on the candles and `3fr` on the volume, and the price
   * pane came back a fifth of its height — silently, because both calls
   * succeeded. Effects run in declaration order, which is the same guarantee the
   * build effect already relies on to run before any of these setters.
   *
   * Its own effect rather than a branch of the heights one, because the two are
   * independent and each has to survive the other being unchanged: two panes of
   * equal height swap and `heightsKey` does not move, while a divider drag
   * changes every height and no ids.
   *
   * The order is read off `groupRef` for the reason the heights are — the effect
   * is keyed on a string, and the ref is what holds the value that string
   * summarises.
   */
  useEffect(() => {
    builderRef.current?.setPaneOrder(groupRef.current.panes.map((pane) => pane.id));
  }, [paneOrderKey]);

  /**
   * Apply pane heights without rebuilding.
   *
   * Covers both directions: the live drag (which also calls this through
   * `resizePanes`) and any height that arrives from elsewhere — a reload, the
   * agent, or an undo — which would otherwise never reach the surface now that
   * a height change no longer re-runs the build effect.
   */
  useEffect(() => {
    builderRef.current?.setPaneHeights(heightsOf(groupRef.current));
  }, [heightsKey]);

  /**
   * Restyle on a theme switch — never rebuild.
   *
   * `applyTheme` re-resolves the chart chrome and every series color in place,
   * so the loaded bars and the zoom window survive (D13). Keying on the theme
   * object is safe because the built-in themes are module constants and
   * `themeById` hands back the same instance for the same ref — the identity
   * changes exactly when the selected theme does.
   *
   * A no-op before the async build completes is fine — the builder was
   * constructed with this theme, so the build paints it anyway.
   */
  useEffect(() => {
    builderRef.current?.applyTheme(theme);
  }, [theme]);

  /**
   * Restyle on a gridline change — never rebuild, for the same reason as the
   * theme above. Solid vs dotted is a dash array on axes that already exist, and
   * `gridLines` is deliberately absent from `structuralKey` so changing it
   * cannot cost a WebGL teardown and a refetch.
   *
   * It has to run *after* `applyTheme` when both change at once, because
   * applying a theme rewrites the axes' gridline styles from a provider that has
   * no notion of a dash. Effects run in declaration order, so this being second
   * is what keeps dotted gridlines dotted across a theme switch — the builder
   * re-applies them internally too, so neither side relies on the other.
   */
  useEffect(() => {
    builderRef.current?.setGridLines(group.gridLines ?? "solid");
  }, [group.gridLines]);

  /**
   * Restyle plots in place — never rebuild.
   *
   * Changing a colour, a width or a dash on an indicator already on the chart is
   * a repaint by exactly the same argument as the theme and the gridlines, so
   * `style`/`palette` are excluded from `structuralKey` and arrive here instead.
   * Without this pair the picker's style controls would each cost a WebGL
   * teardown and a data refetch, which is the failure this whole file is shaped
   * around.
   *
   * Every plot is re-applied rather than only the one that changed: the key is a
   * value signature, so this runs when *something* moved and the builder's own
   * writes are idempotent — cheaper than tracking which plot it was.
   *
   * It runs after `applyTheme` (declaration order) for the same reason
   * `setGridLines` does: applying a theme re-resolves every series from the
   * stored pane models, so anything applied before it would be overwritten.
   */
  useEffect(() => {
    const builder = builderRef.current;
    if (!builder) return;
    for (const { id, style, palette } of stylesOf(groupRef.current, defaultCandleStyle())) {
      builder.applyStyle(id, style, palette);
    }
  }, [stylesKey, theme]);

  /**
   * Show and hide plots in place — never rebuild, and never refetch.
   *
   * `hidden` is excluded from `structuralKey` like every other appearance
   * setting, and the argument is sharper here than for a colour: routing it
   * through a rebuild would throw away the bars and fetch them again *in order
   * to draw nothing*, and unhiding would cost a second round trip. A hidden plot
   * stays fed, so flicking between two indicators is two property writes.
   *
   * Runs after the restyle effect for `setGridLines`' reason — `applyTheme`
   * re-resolves every series from the stored pane models, so anything applied
   * before it would be overwritten.
   */
  useEffect(() => {
    const builder = builderRef.current;
    if (!builder) return;
    for (const { id, hidden } of hiddenOf(groupRef.current)) {
      builder.setPlotHidden(id, hidden);
    }
  }, [hiddenKey, theme]);

  /**
   * Axis drag and zoom direction, applied without rebuilding.
   *
   * Both map onto properties of modifiers already attached to the surface, so
   * they are excluded from `structuralKey` like everything else here. Reading
   * from `groupRef` keeps the effect keyed on the two values rather than on the
   * group object.
   */
  useEffect(() => {
    builderRef.current?.setInteractions(resolveInteractions(groupRef.current));
  }, [interactionsKey]);

  /**
   * Widen or narrow the window — never rebuild, and never refetch.
   *
   * This is the effect the whole feature turns on. `visibleBars` is deliberately
   * absent from `structuralKey`, so changing it costs one range write on an axis
   * that already exists; putting it in the key would have made every keystroke
   * in the count box a WebGL teardown and a fresh request for bars the browser
   * was already holding.
   *
   * It **holds the right edge** rather than jumping to the latest bar. Asking to
   * see more bars while reading 2021 should show more of 2021, not four years of
   * travel — see `windowHoldingRight`. The controls that do mean "ending now"
   * (the presets, jump-to-latest) say so themselves by anchoring at the end.
   */
  useEffect(() => {
    applyWindow({ at: "hold" });
  }, [visibleBars, applyWindow]);

  /**
   * Empty space to the right of the last bar.
   *
   * Two writes, because SciChart splits the job: `growBy` on the axis covers the
   * paths where it computes a range for itself (the initial fit, a
   * zoom-extents), and the window we set explicitly carries the same margin in
   * its own `max` — `growBy` is applied when a range is *derived*, not when one
   * is handed over.
   */
  useEffect(() => {
    builderRef.current?.setRightMargin(rightMargin);
    applyWindow({ at: "hold" });
  }, [rightMargin, applyWindow]);

  /**
   * Scale the price axis to the bars, or to everything drawn on them.
   *
   * Its own effect keyed on the value, exactly as the theme, the gridlines and
   * the margin are, and excluded from `structuralKey` for the same reason: it
   * re-ranges an axis that already exists, and routing it through the rebuild
   * would trade a repaint for a WebGL teardown plus a refetch.
   */
  useEffect(() => {
    builderRef.current?.setPriceFit(group.priceFit ?? "price");
  }, [group.priceFit]);

  /**
   * The menu's *Manual* and *Price only* / *All plots*, reaching this chart's
   * builder — see `lib/y-axis-mode.ts`. Picking an auto choice that is already
   * the setting changes nothing in the model, so it cannot ride the effect above.
   */
  useEffect(
    () => onYAxisMode(group.id, (mode) => builderRef.current?.setYManual(mode === "manual")),
    [group.id],
  );

  /**
   * Axis ranges, applied as property writes.
   *
   * Keyed on a JSON string for `stylesKey`'s reason: the array is rebuilt every
   * render, so an identity dependency would fire the effect on every render
   * whether or not a range moved.
   */
  useEffect(() => {
    const builder = builderRef.current;
    if (!builder) return;
    for (const { id, range } of axisRangesOf(group)) {
      builder.setAxisRange(id, range as { min: number; max: number } | undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axisRangesKey]);

  /**
   * **Seasonality hits**: its pane's scale is a count of years, zero in the
   * middle (`lib/seas-hits.ts`) — applied after the contract's ranges and only
   * to an axis the user has not ranged themselves.
   */
  useEffect(() => {
    const builder = builderRef.current;
    if (!builder) return;
    const ranged = new Set(axisRangesOf(group).filter((a) => a.range).map((a) => a.id));
    for (const pane of group.panes) {
      const axis = hitsAxisId(pane);
      if (!axis || ranged.has(axis)) continue;
      const range = hitsRange(geometry, pane.plots);
      if (range) builder.setAxisRange(axis, range);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geometry, group.panes, axisRangesKey, structure]);

  /**
   * The user's drawings, pushed to the surface whenever they change.
   *
   * Excluded from `structuralKey` on purpose and for the usual reason: adding a
   * trendline must not tear down a WebGL surface and refetch the data. Someone
   * drawing five lines would otherwise pay five refetches.
   *
   * `derived` is `undefined` here for now — the trading plan's levels and the
   * fill marks are computed elsewhere and are the next thing to arrive; the
   * argument exists so they cannot be smuggled in through `Pane.annotations`
   * when they do.
   */
  useEffect(() => {
    const builder = builderRef.current;
    if (!builder) return;
    // **Bar Intelligence**, measured against the bars the chart holds right
    // now (`lib/bar-intelligence-view.ts`). Computed here because this is the
    // one place that already re-runs on a new load, a new frame of shapes and
    // — through `tickStamp` — a live tick; the data panel reads the same
    // answer through the store rather than computing a second one.
    const biBars = chartBars(dataRef.current, realBarsRef.current);
    // The inside setup counts settled bars only — the forming one's range still
    // moves — by the rule the inside/outside boxes use.
    const biPeriod = periodSeconds(group.frequency);
    const biLast = biBars.t[biBars.t.length - 1];
    const biForming =
      ticking && biLast !== undefined && (biPeriod === undefined || !barOver(biLast, biPeriod, Date.now() / 1000));
    const barIntel = barIntelFor(group, biBars, geometry, biBars.t.length - (biForming ? 1 : 0));
    publishBarIntel(group.id, barIntel?.snapshot);
    for (const pane of group.panes) {
      // The journal draws on the price pane and nowhere else: an entry is a
      // price, and the same number against a volume axis is a line at the
      // bottom of the pane meaning nothing.
      //
      // Pattern shapes join them there, from the latest response — derived too,
      // so they are rebuilt every render and never saved.
      //
      // **A pane of its own draws its own geometry plots** — Seasonality hits
      // (2026-10-22) is the first shape that is not on price. Against the full
      // x, so a block on the future rows the server added is placed like any.
      const own = pane.role === "price" ? [] : geometryAnnotations(geometry, pane.plots, xRef.current);
      const derived =
        pane.role === "price"
          ? [
              ...(journalAnnotations ?? []),
              ...geometryAnnotations(geometry, pane.plots, xRef.current),
              ...(barIntel?.annotations ?? []),
            ]
          : own.length > 0
            ? own
            : undefined;
      // **Filtered to this symbol and interval.** A drawing describes one
      // instrument's price action; unfiltered it followed the chart onto the
      // next symbol and sat at the same prices over different bars, straight
      // and describing nothing. Drawings made before the stamp existed carry
      // neither field and are shown everywhere, which is what they did then.
      //
      // Filtered here rather than dropped on a symbol change, because the
      // drawings are *kept*: charting AAPL again brings its lines back.
      builder.setAnnotations(
        pane.id,
        annotationsFor(pane.annotations, group),
        derived,
        journalEditable,
      );
    }
  }, [group.panes, group.symbol, group.frequency, barStamp, tickStamp, journalAnnotations, journalEditable, geometry]);

  // The panel must not go on describing a chart that is gone.
  useEffect(() => () => publishBarIntel(group.id, undefined), [group.id]);

  /**
   * A dragged plan level, reported upwards.
   *
   * Through a ref for `onAnnotationDrawn`'s reason: the handler closes over a
   * callback whose identity changes every parent render, and depending on it
   * here would re-register — or worse, rebuild — on every one.
   */
  /**
   * Delete removes the selected drawings.
   *
   * On `window` rather than the chart, because a WebGL canvas is not focusable
   * and a selection made by clicking it leaves focus wherever it was — a
   * listener on the container would never fire.
   *
   * Guarded on the event target so it cannot eat a Delete meant for a text box:
   * the symbol search, the plan form and the screener's expression editor are
   * all inputs where Delete means delete a character.
   */
  const deletedRef = useRef(onAnnotationsDeleted);
  deletedRef.current = onAnnotationsDeleted;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Delete" && event.key !== "Backspace") return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target?.isContentEditable) {
        return;
      }
      // Removes the placed ones itself and hands back the contract ids, so one
      // key deletes a drawing whichever library drew it — the user selected *a
      // drawing* and does not know the difference.
      const removed = builderRef.current?.deleteSelected() ?? [];
      event.preventDefault();
      if (removed.length > 0) deletedRef.current?.(removed);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /**
   * Which drawings are selected, so the style bar can be about one of them.
   *
   * Ids rather than the annotations themselves: the builder replaces its
   * annotation objects on every render, so anything held here would be a copy of
   * something the workspace owns and the copy is what would go stale. Resolved
   * against `panes` on the way out, below.
   *
   * Subscribed once and never re-subscribed — the builder outlives every restyle
   * (a style change is a repaint, so it never rebuilds), and the handler only
   * calls `setState`.
   */
  /**
   * Escape takes back a placed point and leaves the tool armed.
   *
   * **Here rather than in the host, and cancelling rather than reporting.** The
   * host owns disarming, which is app-wide; only this component knows whether
   * *its* chart is the one holding a point. A chart with nothing placed does
   * nothing, so every chart can listen and exactly one will act — where a host
   * asking "is anything placed" would have to aggregate across charts to answer
   * a question with one true owner.
   *
   * The two listeners do not race: the host's own Escape reads the `placing` it
   * was last told, which is the value from before this keystroke, so it declines
   * to disarm on the same press that takes the point back.
   */
  const placingRef = useRef(onPlacing);
  placingRef.current = onPlacing;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      builderRef.current?.cancelPlacement();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const builder = builderRef.current;
    if (!builder) return;
    builder.onPlacingChanged((placing) => placingRef.current?.(placing));
    return () => builder.onPlacingChanged(null);
  }, [structure]);

  const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);
  useEffect(() => {
    const builder = builderRef.current;
    if (!builder) return;
    builder.onAnnotationSelected((ids) => setSelectedIds(ids));
    return () => builder.onAnnotationSelected(null);
  }, [structure]);

  /**
   * The selected drawings, as the workspace has them.
   *
   * Only the user's own: a selected id that matches nothing in `panes` is a
   * derived level or one of the library's placed drawings, and neither is the
   * user's to restyle — the same rule `deleteSelected` follows.
   */
  const selectedAnnotations = useMemo(() => {
    if (selectedIds.length === 0) return [];
    const byId = new Map(
      group.panes.flatMap((pane) => (pane.annotations ?? []).map((a) => [a.id, a] as const)),
    );
    return selectedIds.map((id) => byId.get(id)).filter((a): a is Annotation => a !== undefined);
  }, [selectedIds, group.panes]);

  // Skips the first run: mounting is not a clear, and clearing on mount would
  // wipe the placed drawings every time the chart rebuilt.
  const clearedAt = useRef(clearNonce);
  useEffect(() => {
    if (clearNonce === clearedAt.current) return;
    clearedAt.current = clearNonce;
    builderRef.current?.clearPlacedDrawings();
  }, [clearNonce]);

  // Through a ref for `onAnnotationDrawn`'s reason: the handler closes over a
  // callback whose identity changes on every parent render.
  const planRef = useRef(onPlanDrawn);
  planRef.current = onPlanDrawn;
  useEffect(() => {
    const builder = builderRef.current;
    if (!builder) return;
    builder.onPlanDrawn((entry, stop, at) => planRef.current?.(entry, stop, at));
    return () => builder.onPlanDrawn(null);
  }, [structure]);

  const movedRef = useRef(onLevelMoved);
  movedRef.current = onLevelMoved;
  useEffect(() => {
    const builder = builderRef.current;
    if (!builder) return;
    builder.onAnnotationEdited((_paneId, annotationId, y) => movedRef.current?.(annotationId, y));
    return () => builder.onAnnotationEdited(null);
  }, [structure]);

  /**
   * Arm the pointer.
   *
   * Only the tool reaches the builder — the palette lives in `App` and serves
   * whichever chart is active, so an inactive chart is passed `null` by its
   * parent rather than deciding for itself.
   */
  useEffect(() => {
    builderRef.current?.setDrawingTool(drawingTool);
  }, [drawingTool]);

  // The financial-tools palette, armed the same way and by its own modifier.
  // Two effects rather than one, because the two are independent: disarming a
  // built-in must not cancel a half-placed channel.
  useEffect(() => {
    builderRef.current?.setTradingTool(tradingTool);
  }, [tradingTool]);

  /**
   * Where a finished drawing goes.
   *
   * Through a ref rather than re-registering per render: the handler closes over
   * a callback that changes identity on every parent render, and re-registering
   * inside the build effect would put it in that effect's dependencies and
   * rebuild the whole surface whenever the parent re-rendered.
   */
  const drawnRef = useRef(onAnnotationDrawn);
  drawnRef.current = onAnnotationDrawn;
  useEffect(() => {
    const builder = builderRef.current;
    if (!builder) return;
    builder.onAnnotationDrawn((paneId, annotation) => drawnRef.current?.(paneId, annotation));
    return () => builder.onAnnotationDrawn(null);
  }, [structure]);

  /**
   * Centre on a date — and fetch back to it when the history stops short.
   *
   * Clamping to the oldest loaded bar was the alternative and is the failure
   * this codebase keeps choosing against: it shows *a* chart, correct-looking,
   * that is not the one asked for. Raising `barsRange` refetches through the
   * ordinary structural rebuild, and `pendingCenterRef` is what carries the
   * intent across it.
   */
  useEffect(() => {
    if (!goTo) return;
    setNotice(null);
    const wanted = parseDateInput(goTo.date);
    if (wanted === undefined) return;

    const x = xRef.current;
    if (x.length > 0 && wanted >= x[0]!) {
      applyWindow({ at: "bar", index: Math.min(indexAtOrAfter(x, wanted), x.length - 1) });
      return;
    }

    // **Before the first bar of a complete load, go to the first bar.** There
    // is nothing further back to fetch — the load is the whole file — so the
    // alternative is a refetch of the same bytes followed by the same answer.
    // Centred half a window in, so the first bar sits at the left edge rather
    // than in the middle of an empty half.
    if (completeRef.current && x.length > 0) {
      const half = Math.floor(visibleBarsOf(groupRef.current) / 2);
      applyWindow({ at: "bar", index: Math.min(half, x.length - 1) });
      setNotice(`history starts ${new Date(x[0]! * 1000).toISOString().slice(0, 10)}`);
      return;
    }

    const plan = planDate({
      startSec: wanted,
      x,
      barsRange: groupRef.current.barsRange,
      // Half of it lands left of the date once it is centred, so it has to be
      // fetched as well: the span alone puts the date on the oldest bar the
      // server sent, with the left half of the chart empty.
      visibleBars: visibleBarsOf(groupRef.current),
      label: goTo.date,
      frequency: groupRef.current.frequency,
      ...(isIntraday(groupRef.current.frequency) ? { maxBars: MAX_INTRADAY_BARS } : {}),
    });
    if (plan.barsRange === undefined) {
      setNotice(plan.refused ?? "that date is outside the loaded history");
      return;
    }
    pendingCenterRef.current = wanted;
    raiseBars(plan.barsRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goTo?.nonce]);

  /**
   * What each span preset would cost, recomputed when a load changes the answer.
   *
   * Evaluated at render rather than on click because the *button* has to say
   * whether it can be honoured: YTD on a five-minute chart is more bars than the
   * server will send, and a disabled control with the numbers in its tooltip
   * beats an enabled one that quietly shows two months of a year.
   */
  /**
   * How many years a seasonal plot actually used, when that is not what it was
   * asked for.
   *
   * Printed only when it differs from the request, and always for `all` — where
   * there is no number in the alias, so the count is the only place it appears
   * at all. A chart that asked for ten and got ten says nothing, because the
   * heading already reads `10`.
   *
   * The alternative was to always print it, which turns a footnote about an
   * unusual case into furniture on every seasonal chart.
   */
  const seasonalNote = useCallback(
    (plotId: string): string | undefined => {
      const used = labelsRef.current["seasYears"];
      if (!used) return undefined;
      const alias = groupRef.current.panes
        .flatMap((pane) => pane.plots ?? [])
        .find((plot) => plot.id === plotId)?.outputAlias;
      if (!alias?.startsWith("seas")) return undefined;
      // `years` is the family's last parameter in both entries, which is what
      // makes one rule cover the path (`seasPath_ytd_10`) and the cone
      // (`seasWin_30_0_all`).
      const asked = alias.split("_").pop();
      return asked === used ? undefined : `→ ${used}y`;
    },
    [],
  );

  const spans = useMemo(
    () =>
      (Object.keys(SPAN_LABELS) as SpanPreset[]).map((preset) => ({
        preset,
        label: SPAN_LABELS[preset],
        plan: planSpan({
          startSec: spanStart(preset, new Date()),
          // Read through the ref on purpose: `barsLoaded` is the state that says
          // *when* this is worth recomputing, and copying five thousand
          // timestamps into React state to say the same thing would cost more
          // than the search it feeds.
          //
          // **The bars only.** A span is measured over sessions that happened —
          // "how far back does a year reach" — and `barsBackTo` also estimates
          // bar *density* from the two ends of what it is given, so a tail of
          // projected dates would both lengthen the count and thin the estimate.
          x: xRef.current.slice(0, realBarsRef.current),
          barsRange: group.barsRange,
          label: SPAN_LABELS[preset],
          frequency: group.frequency,
          // An intraday chart may load more per request than a daily one
          // would ever need to (`QV_INTRADAY_MAX_BARS`).
          ...(isIntraday(group.frequency) ? { maxBars: MAX_INTRADAY_BARS } : {}),
          // Read beside `realBarsRef` and moved by the same load, so
          // `barsLoaded` already says when it changed.
          complete: completeRef.current,
        }),
      })),
    [barsLoaded, group.barsRange, group.frequency],
  );

  /**
   * Take a preset: move the window, and ask for more bars only if it needs them.
   *
   * The window is applied here *as well as* through the effect above, because
   * the effect holds the right edge and a preset means "ending now". When the
   * plan raises `barsRange` there is nothing to apply yet — the rebuild is
   * already on its way, and the load will anchor at the end for us.
   */
  const takeSpan = useCallback(
    (plan: (typeof spans)[number]) => {
      if (plan.plan.visibleBars === undefined) {
        setNotice(plan.plan.refused ?? null);
        return;
      }
      setNotice(null);
      if (plan.plan.barsRange !== undefined) raiseBars(plan.plan.barsRange, plan.plan.visibleBars);
      else onWindow?.({ visibleBars: plan.plan.visibleBars });
      if (plan.plan.barsRange === undefined) {
        applyWindow({ at: "end" }, plan.plan.visibleBars);
      }
    },
    [applyWindow, onWindow],
  );

  /**
   * Move the window without resizing it — ‹ and ›.
   *
   * Nothing is dispatched: where a chart is scrolled to is session state (D8),
   * the same as a wheel pan, and a save per press would write the workspace
   * four times a second on a held button.
   */
  const pan = useCallback(
    (direction: -1 | 1) => {
      setNotice(null);
      // The step is a quarter of what is **on screen**, measured inside
      // `applyWindow` from the live range — see the pan branch there.
      applyWindow({ at: "pan", direction });
    },
    [applyWindow],
  );

  /**
   * Zoom by changing how many bars are on screen — 🔍− and 🔍+.
   *
   * This *is* dispatched, because it is the same number the bars-visible box
   * holds and it persists with the chart. Applied here as well as through the
   * effect on `visibleBars`, for `takeSpan`'s reason: the dispatch is a render
   * away, and the effect that follows it holds the right edge anyway.
   */
  const zoom = useCallback(
    (direction: "in" | "out") => {
      // From the live range when there is one, for `pan`'s reason: after a
      // wheel zoom the stored count is not what anyone is looking at.
      const now = liveBars() ?? visibleBarsOf(groupRef.current);
      const next = zoomedBars(now, direction, realBarsRef.current);
      if (next === now) return;
      setNotice(null);
      onWindow?.({ visibleBars: next });
      applyWindow({ at: "hold" }, next);
    },
    [applyWindow, onWindow],
  );

  /**
   * Repositioning the SubCharts directly keeps the drag at pointer speed: going
   * through the workspace would re-render the tree on every move, and the effect
   * above would rebuild the surface — dropping the loaded series and the zoom
   * window mid-drag. The workspace write happens once, on release.
   */
  const resizePanes = useCallback((heights: string[]) => {
    builderRef.current?.setPaneHeights(heights);
  }, []);

  /**
   * Which pane the pointer is over, for the reorder arrows.
   *
   * **Read off the container, not off the overlay**, and that is the whole
   * design: a per-pane band with `pointerEvents` on would find the hover and
   * cover the entire canvas doing it, leaving a chart that cannot be panned or
   * zoomed. A `pointermove` bubbles up here from the canvas instead, so nothing
   * is intercepted and the overlay stays inert.
   *
   * The setter is called on every move and re-renders on almost none of them:
   * React bails out when the value is unchanged, and it only changes when the
   * pointer crosses a seam.
   */
  /**
   * The company events marked along the foot of the price pane.
   *
   * Fetched here rather than lifted, because the one thing they depend on is
   * this chart's own symbol — and both loaders are module-level caches keyed by
   * symbol, so two charts on the same name pay for one request.
   *
   * Gated on the setting rather than fetched-and-hidden: with the markers off
   * there is nothing to draw, and a request nobody asked for is a request.
   */
  const showEarnings = events?.earnings ?? false;
  const showNews = events?.news ?? false;
  const [quarters, setQuarters] = useState<readonly EarningsQuarter[]>([]);
  const [headlines, setHeadlines] = useState<readonly string[]>([]);
  const [currency, setCurrency] = useState<string | undefined>(undefined);
  // The report not yet made, for the yellow `E` past the last bar (owner, 2026-10-28).
  const [upcoming, setUpcoming] = useState<UpcomingEarnings | null>(null);
  // An EPS / revenue YoY or QoQ plot draws the next report's estimate in yellow
  // (owner, 2026-10-29), so it needs the next report even with the markers off.
  const growthPlots = useMemo(
    () =>
      group.panes
        .flatMap((pane) => pane.plots)
        .filter((p): p is NonNullable<typeof p> => Boolean(p?.outputAlias && ESTIMATED_SERIES[p.outputAlias])),
    [group.panes],
  );
  const wantsUpcoming = showEarnings || growthPlots.length > 0;

  useEffect(() => {
    if (!wantsUpcoming) {
      setQuarters([]);
      setUpcoming(null);
      return;
    }
    let live = true;
    // `depth: false` — the quarters come off an artifact, so nothing here needs
    // the two per-symbol vendor calls the depth panel pays for.
    void loadFundamentals(group.symbol, false).then((document) => {
      if (!live) return;
      setQuarters(document?.earnings ?? []);
      setUpcoming(upcomingEarnings(document));
      // The currency the revenue figures are in, from the same document that
      // carries them — never guessed from the symbol's suffix.
      setCurrency(document?.currency);
    });
    return () => {
      live = false;
    };
  }, [group.symbol, wantsUpcoming]);

  useEffect(() => {
    if (!showNews) {
      setHeadlines([]);
      return;
    }
    let live = true;
    void loadNews(group.symbol).then((news) => {
      if (live) setHeadlines((news?.articles ?? []).slice(0, 4).map((a) => a.title));
    });
    return () => {
      live = false;
    };
  }, [group.symbol, showNews]);

  /**
   * The glyphs, rebuilt whenever their inputs move.
   *
   * `barsLoaded` is in the dependency list and `xRef` is not: the timestamps
   * live in a ref because five thousand numbers that gain one per realtime bar
   * have no business in React state, so the count beside them is what says they
   * changed. Reading the ref here is reading the value that count summarises —
   * the same pairing `applyWindow` already uses.
   */
  const eventMarkers = useMemo<EventMarker[]>(() => {
    // **Real bars only.** `xRef` deliberately holds the projection past the last
    // candle as well, so its final entry can be a year into the future — which
    // would hang the news marker out in empty space to the right of everything,
    // and would let a snap land on a bar that has not happened. Announcements
    // are all in the past, so nothing is lost by cutting the forecast off.
    const bars = xRef.current.slice(0, realBarsRef.current || xRef.current.length);
    const out = showEarnings ? earningsMarkers(quarters, bars) : [];
    // The next report is the one marker that *is* in the future, so it is placed
    // against the whole axis, projection included.
    const next = showEarnings ? upcomingMarker(upcoming, bars, xRef.current) : null;
    if (next) out.push(next);
    const news = showNews ? newsMarker(bars, headlines.length) : null;
    return news ? [...out, news] : out;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `xRef` via `barsLoaded`
  }, [quarters, upcoming, headlines, showEarnings, showNews, barsLoaded]);

  const markersKey = eventMarkers.map((m) => `${m.id}@${m.time}:${m.tone}`).join("|");

  // Mirrored for the build effect, which is keyed on `structure` and would
  // otherwise close over the markers of whatever render it was created in.
  const markersRef = useRef<EventMarker[]>(eventMarkers);
  markersRef.current = eventMarkers;

  // The next report's estimate on each reported-growth plot, from the future
  // `E`'s own time — placed whether or not that marker is shown.
  const estimates = useMemo<EstimateLine[]>(() => {
    const bars = xRef.current.slice(0, realBarsRef.current || xRef.current.length);
    return estimateLines(upcoming, upcomingMarker(upcoming, bars, xRef.current)?.time, growthPlots);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `xRef` via `barsLoaded`
  }, [upcoming, growthPlots, barsLoaded]);
  const estimatesKey = estimates.map((e) => `${e.plotId}@${e.time}:${e.value}`).join("|");
  const estimatesRef = useRef<EstimateLine[]>(estimates);
  estimatesRef.current = estimates;

  useEffect(() => {
    builderRef.current?.setEstimateLines(estimates);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by value
  }, [estimatesKey]);

  useEffect(() => {
    builderRef.current?.setEventMarkers(eventMarkers);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by value
  }, [markersKey]);

  /**
   * Who to tell when the pointer finds a glyph.
   *
   * Re-subscribed on every rebuild, because a rebuild mints a new builder and
   * the old one's handler died with it — `structure` is in the list for exactly
   * that, and it is the same reason the markers themselves are a `buildGroup`
   * argument.
   */
  useEffect(() => {
    builderRef.current?.onEventMarkerHover((marker, at) => {
      setHoveredEvent(marker && at ? { marker, at } : null);
    });
    return () => builderRef.current?.onEventMarkerHover(null);
  }, [structure]);

  /**
   * A marker stops being hovered when it stops existing.
   *
   * Turning the setting off, or changing symbol with the popup open, removes the
   * glyph under the pointer without a pointer event — so the panel would hang
   * there describing a quarter that is no longer on the chart.
   */
  useEffect(() => {
    setHoveredEvent((open) =>
      open && eventMarkers.some((m) => m.id === open.marker.id) ? open : null,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by value
  }, [markersKey]);

  const [hoveredPane, setHoveredPane] = useState<string | null>(null);
  const [hoveredEvent, setHoveredEvent] = useState<
    { marker: EventMarker; at: { x: number; y: number } } | null
  >(null);
  /** A Bar Intelligence label under the pointer: every level in its stack. */
  const [hoveredLevels, setHoveredLevels] = useState<
    { lines: readonly string[]; at: { x: number; y: number } } | null
  >(null);
  useEffect(() => {
    builderRef.current?.onDerivedHover((lines, at) => {
      setHoveredLevels(lines && at ? { lines, at } : null);
    });
    return () => builderRef.current?.onDerivedHover(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-subscribed per build, as the markers are
  }, [structure]);
  const trackPaneHover = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      if (rect.height === 0) return;
      setHoveredPane(paneAtFraction(groupRef.current.panes, (event.clientY - rect.top) / rect.height));
    },
    [],
  );

  /**
   * The **bottom** of the price pane, as a fraction of the chart's height.
   *
   * Derived from the same `stackFractions` the renderer stacks the panes with,
   * so the buttons cannot drift off the pane they belong to when a divider
   * moves — the same reason `PaneDividers` takes its seams from `paneBoundaries`
   * rather than computing its own.
   *
   * The strip sits just inside that edge rather than on it: flush against the
   * seam it would touch the pane below, and a control that straddles two panes
   * reads as belonging to neither.
   *
   * **The price pane is found by role, not by index.** This read
   * `stackFractions(...)[0]` while the price pane was always first, which is a
   * different question that happened to have the same answer — and stopped
   * having it the moment panes could be reordered, sending the strip and the PIP
   * onto whatever pane had been moved to the top. `priceBottom` above is the
   * same reading, so this is now its percentage rather than a second one that
   * could drift.
   */
  const priceBandBottom = priceBottom * 100;

  /**
   * The state of the feed, said plainly, and only when it is not the good one.
   *
   * Deliberately **not** routed through `error`, which is prefixed "chart error:"
   * and coloured as one: a dropped socket is a blip the client is already
   * retrying, and colouring an ordinary blip red teaches people to distrust the
   * red — the same argument `notice` was split out for.
   *
   * Worded for what is knowable rather than for what is likely. "reconnecting…"
   * would be a guess on `closed`, and a guess that is wrong in exactly the case
   * that matters: a socket the transport has stopped retrying. "disconnected" is
   * true either way, and the retry shows itself when the state flips to
   * `connecting` a moment later. `idle` says nothing because nothing has been
   * attempted yet, and the offline fixture opens instantly — so on the demo
   * workspace this never renders at all.
   */
  const feedLabel =
    feed === "connecting" ? "connecting…" : feed === "closed" ? "disconnected" : null;

  // Turning the legend off with the pointer over the chart leaves the last bar
  // it read on screen: the handler returns early from then on, so nothing else
  // ever clears it. Cheap because it only runs when the setting itself moves.
  useEffect(() => {
    if (group.valueLegend ?? true) return;
    hoverRef.current = null;
    setHoverIndex(null);
  }, [group.valueLegend]);

  return (
    <div
      style={{ position: "relative", width: "100%", height: "100%", minHeight: 0 }}
      // Bubbled from the canvas, and deliberately not `onPointerEnter`: the
      // arrows need to know *which* pane, which only a coordinate can say. Both
      // are omitted when nothing can be reordered, so a read-only chart pays
      // nothing per pointer move.
      onPointerMove={onPaneOrder ? trackPaneHover : undefined}
      onPointerLeave={onPaneOrder ? () => setHoveredPane(null) : undefined}
    >
      {/*
        Two divs, deliberately. SciChart's `delete()` calls `clearRootElement`,
        which does `style.position = ""` and `style.background = ""` on the div it
        was handed. Give it the positioned one and a dispose strips the
        `position: absolute` that `inset: 0` depends on — the div collapses to
        auto height, and the *next* surface measures a container that no longer
        fills its parent. That is the gap under the chart, and it appears only on
        the paths that dispose (symbol, frequency, chart type), never on a drag.
        React does not repair it either: the style prop is unchanged between
        renders, so it writes nothing.

        So the outer div owns the layout and SciChart never sees it; the inner one
        is SciChart's to mutate, and sizes itself by width/height rather than by
        the `position` that gets cleared.
      */}
      <div style={{ position: "absolute", inset: 0, background: "var(--qf-background)" }}>
        {/*
          The `id` is load-bearing, and its absence is what made the second chart
          on a layout blank. SciChart names its WASM render destination after the
          host div — `${div.id}_2D` — so two charts whose divs both have no id
          are both `_2D`, and `createChildSurface` treats the new one as a
          re-create of the old: it deletes the first surface's destination before
          registering its own. The first chart then keeps its canvas, its panes
          and its loaded bars, and is simply never drawn to again. Nothing throws,
          because SciChart's own "provide a unique ID for each chart div" check
          runs only when a *string* id is passed to `create`, never for an element.
        */}
        <div
          id={surfaceId}
          ref={containerRef}
          onContextMenu={(event) => {
            // Only when a level is actually under the pointer. Anywhere else
            // the chart's own menu should open, so the event is left to bubble
            // — a right-click on empty chart offering "add target" would be
            // offering to edit a plan the user was not pointing at.
            const hit = builderRef.current?.levelAt(event.clientX, event.clientY);
            if (!hit) return;
            event.preventDefault();
            event.stopPropagation();
            onLevelMenu?.(hit.id, event.clientX, event.clientY);
          }}
          style={{
            width: "100%",
            height: "100%",
            // The only thing saying the next drag will draw rather than pan.
            // On the container rather than the canvas: SciChart owns the canvas
            // element and a sub-surface has no DOM node of its own to style.
            cursor: drawingTool === null ? undefined : "crosshair",
          }}
        />
        {/* **Opaque while pending, not only when blocked.**

            This is what actually enforces the requirement. The surface has to be
            built for SciChart to decide anything at all, so between construction
            and the verdict there is a window in which a refused licence would be
            drawing its notice underneath — and this covers it. Committed in the
            same render as the container above, while the surface is created in an
            effect that runs after paint, so the cover is never late.

            `--qf-background` matches the chart's own ground, so the ordinary case
            is an empty pane that fills in rather than a flash of something else. */}
        {licence?.charts !== "allowed" && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "var(--qf-background)",
              // Above the canvas. Below the toolbars and overlays, which are
              // siblings of the container rather than children of it.
              zIndex: 1,
            }}
          >
            {licence?.charts === "blocked" && <ChartsUnavailable why={licence.why} />}
          </div>
        )}
      </div>
      {/* Over the chart, and only while something is selected — it is about one
          drawing rather than about the chart, so it cannot live in the tool strip.
          Appearing with the selection is also the only affordance that says a
          drawing *can* be restyled. */}
      {onAnnotationStyled && (
        <AnnotationStyleBar
          selected={selectedAnnotations}
          resolve={resolveToken}
          onStyle={onAnnotationStyled}
          onDelete={(id) => deletedRef.current?.([id])}
        />
      )}
      <SpanControls
        priceBandBottom={priceBandBottom}
        spans={spans}
        ready={barsLoaded > 0}
        onSpan={takeSpan}
        onLatest={() => {
          setNotice(null);
          // ›| also hands a hand-scaled Y axis back to the fit (owner, 2026-10-16).
          builderRef.current?.setYManual(false);
          applyWindow({ at: "end" });
        }}
        onPan={pan}
        onZoom={zoom}
      />
      {onPaneHeights && (
        <PaneDividers panes={group.panes} onResize={resizePanes} onCommit={onPaneHeights} />
      )}
      {/* After the dividers so a seam handle, the smaller target, wins the
          overlap — the arrows are centred in a band and the seams are on its
          edges, so they only meet on a very short pane. */}
      {/* Last of the overlays, so it paints over the legend and the panel when a
          short pane forces them together — it is transient and about the thing
          under the pointer, which is the newest claim on that space. */}
      {hoveredLevels && <LevelsPopup lines={hoveredLevels.lines} at={hoveredLevels.at} />}
      {hoveredEvent && (
        <EventPopup
          marker={hoveredEvent.marker}
          at={hoveredEvent.at}
          {...(hoveredEvent.marker.id.startsWith(NEXT_EARNINGS_PREFIX) && upcoming
            ? { quarter: upcoming.quarter, growth: upcoming.growth, upcoming: true }
            : {
                quarter: quarterFor(quarters, hoveredEvent.marker.id),
                growth: growthFor(quarters, hoveredEvent.marker.id),
              })}
          headlines={hoveredEvent.marker.id.startsWith("news:") ? headlines : undefined}
          currency={currency}
        />
      )}
      {onPaneOrder && (
        <PaneReorder
          panes={group.panes}
          hovered={hoveredPane}
          // The move is computed here rather than in the overlay: the overlay
          // knows where it was clicked, and `movePane` knows what a move *is*
          // —
          // including that an impossible one hands back the same group by
          // identity, which is what stops a no-op costing a save.
          onMove={(paneId, delta) => {
            const after = movePane(group, paneId, delta);
            if (after !== group) onPaneOrder(after.panes);
          }}
        />
      )}
      <ChartWatermark
        symbol={group.symbol}
        frequency={group.frequency}
        priceBandTop={priceTop * 100}
        priceBandBottom={priceBandBottom}
      />
      {(group.valueLegend ?? true) && (
        <ValueLegend
          panes={group.panes}
          planFor={(paneId) => builderRef.current?.getPanePlan(paneId)}
          values={dataRef.current?.columns ?? {}}
          index={hoverIndex}
          noteFor={seasonalNote}
          readingFor={(plotId, at) => {
            const plot = group.panes.flatMap((p) => p.plots ?? []).find((p) => p.id === plotId);
            if (isSeasHitsAlias(plot?.outputAlias)) return hitsAt(geometry, [plot!], dataRef.current?.x[at]);
            if (!isBarHighlightAlias(plot?.outputAlias)) return undefined;
            const t = dataRef.current?.x[at];
            const kind = t === undefined ? undefined : barKindsRef.current.get(t);
            return kind ? kindLabel(kind) : "";
          }}
          barCount={barsLoaded}
          symbol={group.symbol}
          controls={legendControls}
          left={plotLeft}
        />
      )}
      {!narrow && (
        <PlotChip
          bind={plotChipRef}
          planFor={(paneId) => builderRef.current?.getPanePlan(paneId)}
          values={dataRef.current?.columns ?? {}}
          symbol={group.symbol}
          controls={legendControls}
        />
      )}
      {/* The panel goes after the legend so it paints over it when a short pane
          forces them together — the legend is a reading you can also get from
          the axis, and the panel is the thing you just opened. */}
      {group.pipView && !pipHidden && available("pip") && (
        <ChartPip
          group={group}
          bars={pipBarsRef.current}
          closes={pipCloses}
          priceBandTop={priceTop * 100}
          priceBandBottom={priceBandBottom}
          pricePaneId={group.panes.find((p) => p.role === "price")?.id}
          onView={(view) => onPip?.({ view })}
          onFrequency={(frequency) => onPip?.({ frequency })}
          onCorner={(corner) => onPip?.({ corner })}
          onSize={(size) => onPip?.({ size })}
          // Offered only when it can actually happen: the promoted interval must
          // have a rung above it, or the swap would leave the panel showing the
          // chart's own timeframe. `swapWithChart` is the one that decides, and
          // a button that does nothing is worse than one that is not there.
          onSwap={
            onPip &&
            (() => {
              const shown = pipFrequencyFor(group.frequency, group.pipFrequency);
              return shown && nextCoarser(shown) && swapWithChart(group.frequency, shown)
                ? () => onPip({ swap: true })
                : undefined;
            })()
          }
          onClose={() => onPip?.({ view: null })}
        />
      )}
      {/*
        **One column, top-right of the price pane, for everything the chart has
        to say about itself.**

        They were placed independently and collided two ways. The messages sat
        top-*left*, which is where the value legend now is, so an error landed on
        top of the readings; and the feed badge was offset to `right: 32` to
        clear an inspector handle that has since moved to the bottom strip, so it
        was dodging something that is not there while a message could still
        appear over it.

        A flex column cannot overlap whatever combination is showing —
        disconnected *and* a refused span is an ordinary pair — and it stays
        right whenever one of them moves again. Top-right because that corner is
        the one part of a chart that is reliably empty: price runs along the
        right edge but rarely along the top of its own range.
      */}
      <div
        style={{
          position: "absolute",
          top: 6,
          right: 6,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 4,
          // The span controls' band: above the canvas, below the dividers.
          zIndex: 4,
          // Nothing here is clickable, and a message that swallowed a drag would
          // make the corner of the chart stop panning for as long as it showed.
          pointerEvents: "none",
          // Never more than half the width: a long message must not run back
          // across the pane into the legend it was moved away from.
          maxWidth: "50%",
        }}
      >
      {liveBadgeOnCanvas && live && !feedLabel && <LiveBadge state={live} tick={liveTick} />}
      {feedLabel && (
        <div
          title="This chart is not receiving data. The prices shown are the last that arrived."
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            padding: "0.1rem 0.4rem",
            borderRadius: 4,
            border: "1px solid var(--qf-border)",
            background: "var(--qf-surface)",
            color: "var(--qf-text-secondary)",
            fontSize: "0.7rem",
            whiteSpace: "nowrap",
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              // A flex child, so it would otherwise be squeezed out of round.
              flex: "0 0 auto",
              borderRadius: "50%",
              // Red only once the feed is actually down. A connect in progress
              // is the ordinary opening of a chart and must not read as a fault.
              background:
                feed === "closed" ? "var(--qf-negative)" : "var(--qf-text-muted)",
            }}
          />
          {feedLabel}
        </div>
      )}
      {error && (
        <div
          // The full text on hover, because the box is capped and an error
          // truncated to its first few words is one nobody can act on.
          title={`chart error: ${error}`}
          style={{
            padding: "0.25rem 0.5rem",
            borderRadius: 4,
            background: "var(--qf-surface)",
            border: "1px solid var(--qf-border)",
            color: "var(--qf-negative)",
            fontSize: "0.8rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100%",
            pointerEvents: "auto",
          }}
        >
          chart error: {error}
        </div>
      )}
      {/*
        A refusal, not a fault — a span past the server's ceiling, a date older
        than anything held. Deliberately not routed through `error`, which is
        prefixed "chart error:" and coloured as one: nothing has gone wrong, the
        chart simply cannot be asked for that, and saying so in red teaches
        people to distrust the red.
      */}
      {notice && !error && (
        <div
          title={notice}
          style={{
            padding: "0.25rem 0.5rem",
            borderRadius: 4,
            background: "var(--qf-surface)",
            border: "1px solid var(--qf-border)",
            color: "var(--qf-text-secondary)",
            fontSize: "0.8rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100%",
            pointerEvents: "auto",
          }}
        >
          {notice}
        </div>
      )}
      </div>
      {/*
        The chart's own control strip, at the bottom-right of the **price pane**.

        Not of the container: that spans the whole stack, so `bottom: 6` put the
        strip in the last pane — under the volume or an oscillator, on a chart
        with more than one. The two coincide only when there is a single pane,
        which is exactly why it looked right until it did not.

        So the offset comes from `stackFractions`, the same function the
        renderer stacks panes with and `PaneDividers` derives its seams from.
        Sharing it is the point: a strip positioned by its own arithmetic would
        drift off the pane edge the first time anyone dragged a divider.

        Bottom-right of that pane is where its x axis and y axis meet — the one
        square carrying neither a price label nor a date, because the axes fence
        it off. A flex row rather than absolutely placed buttons, so adding one
        is a child and not a second set of coordinates to keep in step.

        Low opacity until hover, like the span controls: these are affordances
        on someone's chart, not chrome.
      */}
      <div
        style={{
          position: "absolute",
          // `- 26px` is the button (20) plus the gap (6) it sits above the seam.
          top: `calc(${priceBottom * 100}% - 26px)`,
          right: 6,
          display: "flex",
          gap: 4,
          alignItems: "center",
        }}
      >
        <button
          type="button"
          title={`Replay the last ${DEFAULT_SPAN_YEARS} years, then come back here`}
          aria-label="Preview history"
          disabled={previewTarget(xRef.current, visibleBarsOf(group)) === undefined}
          onClick={() => (previewing ? stopPreview() : runPreview())}
          style={{ ...STRIP_BUTTON, opacity: previewing ? 1 : 0.45 }}
          onPointerEnter={(e) => (e.currentTarget.style.opacity = "1")}
          onPointerLeave={(e) =>
            (e.currentTarget.style.opacity = previewing ? "1" : "0.45")
          }
        >
          {/* A square while it runs, because the button is its own cancel — a
              preview is seconds long and waiting it out is not a control. */}
          {previewing ? "■" : "↺"}
        </button>
        <button
          type="button"
          title="Inspect the data the server sent"
          aria-label="Inspect chart data"
          onClick={() => setInspecting(true)}
          style={{
            ...STRIP_BUTTON,
            fontStyle: "italic",
            fontWeight: 700,
            opacity: inspecting ? 1 : 0.45,
          }}
          onPointerEnter={(e) => (e.currentTarget.style.opacity = "1")}
          onPointerLeave={(e) => (e.currentTarget.style.opacity = inspecting ? "1" : "0.45")}
        >
          i
        </button>
      </div>
      {inspecting && (
        <ChartDataInspector
          snapshot={dataRef.current ?? EMPTY_SNAPSHOT}
          // Read live rather than captured: it is the geometry as it is *now*,
          // after any divider drag or resize since the load, which is exactly
          // the state a misalignment has to be judged against.
          panes={builderRef.current?.debugStats() ?? []}
          // The snapshot's arrays are appended to in place, so its identity says
          // nothing about whether it changed. The stamp is what the inspector's
          // memos key on — passed rather than used as a `key`, which would
          // remount the dialog on every realtime bar and drop the row filter
          // someone had just set.
          stamp={dataStamp}
          onClose={() => setInspecting(false)}
        />
      )}
    </div>
  );
}

/**
 * 1Y · YTD · jump-to-latest, floating along the bottom of the price pane.
 *
 * **The placement moved once, deliberately.** It was first built over the middle
 * of the pane; the objection was that the middle is the most valuable area on
 * the screen and a control there covers price action exactly where the eye is,
 * twice over on a two-chart layout. Bottom-centre keeps the float — the reason
 * it is not docked to the time axis is that it belongs to the price pane and
 * follows it when a divider moves — while sitting over the part of the band that
 * is usually empty, since price rarely runs along the floor of its own range.
 *
 * Still a float, so it still obliges the thing a float obliges: the strip **must
 * not swallow gestures it is not over**. `pointer-events: none` on the container and `auto` on each button
 * is what keeps the rest of that band pannable, and it is why this is a row of
 * buttons in a transparent container rather than a panel with a background.
 * They sit at low opacity and come up on hover, so the candles they cover are
 * still readable while the pointer is elsewhere.
 */
/**
 * Height of the date-label band at the foot of the price pane, in px.
 *
 * A measured constant rather than a query: SciChart lays the axis out inside its
 * own WebGL surface, so there is no DOM box to read, and reaching into the
 * surface for it would tie this overlay to the renderer's internals for a number
 * that changes only with the axis font.
 */
const X_AXIS_BAND_PX = 46;

function SpanControls({
  priceBandBottom,
  spans,
  ready,
  onSpan,
  onLatest,
  onPan,
  onZoom,
}: {
  priceBandBottom: number;
  spans: ReadonlyArray<{ preset: SpanPreset; label: string; plan: { visibleBars?: number; refused?: string } }>;
  /** False until bars have landed — nothing can be planned against no data. */
  ready: boolean;
  onSpan: (span: { preset: SpanPreset; label: string; plan: { visibleBars?: number; refused?: string } }) => void;
  onLatest: () => void;
  /** One press: back (-1) or forward (1) by a quarter of the window. */
  onPan: (direction: -1 | 1) => void;
  onZoom: (direction: "in" | "out") => void;
}) {
  return (
    <div
      // `data-tour`: the span presets, which the tour lights beside the
      // interval box — the two together are what "select a period" means.
      // Every button in here is titled by an interpolation (`Show 1Y`, or the
      // refusal reason), so the row is the only stable handle. See lib/tour.ts.
      data-tour="span-presets"
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        // Anchored to the bottom of the price band, lifted clear of the X axis.
        // `translateY(-100%)` puts the strip's own bottom on that line and the
        // margin backs it off, so the offset does not change with the strip's
        // height the way a `top` in percent would.
        //
        // The lift is not a nudge: the **price pane draws the date labels**
        // (`Pane.role === "price"` is defined as the one that does), so the last
        // band of that pane is axis, not chart. At 14px the strip landed on top
        // of "4/21/2026". This clears the labels and their tick gap.
        top: `${priceBandBottom}%`,
        transform: "translateY(-100%)",
        marginTop: -X_AXIS_BAND_PX,
        display: "flex",
        justifyContent: "center",
        gap: 6,
        // The container is a hit-testing hole; only the buttons inside it are
        // solid. Without this the whole band would eat a pan that started in it.
        pointerEvents: "none",
        // Above the chart canvas, below the pane dividers (6) — they overlap
        // nothing, but a seam handle must stay grabbable if a pane is dragged
        // small enough to bring the two together.
        zIndex: 4,
      }}
    >
      {/* **Zoom is leftmost** (owner's order, 2026-09-19), so the strip reads
          outwards from the chart itself: how much of the history is on screen,
          then the two spans that set it outright, then where in the history you
          are — and the group that ends in jump-to-latest is nearest the right
          edge it jumps to. */}
      <FloatingGroup>
        <FloatingButton label={<ZoomIcon sign="out" />} title="Zoom out — show more bars" aria-label="Zoom out" disabled={!ready} grouped onClick={() => onZoom("out")} />
        <FloatingButton label={<ZoomIcon sign="in" />} title="Zoom in — show fewer bars" aria-label="Zoom in" disabled={!ready} grouped onClick={() => onZoom("in")} />
      </FloatingGroup>
      {spans.map((span) => (
        <FloatingButton
          key={span.preset}
          label={span.label}
          disabled={!ready || span.plan.visibleBars === undefined}
          // The reason travels with the disabled control, which is the whole
          // point of refusing in the plan rather than at the click: a button
          // that says why it cannot do something beats one that pretends.
          title={span.plan.refused ?? `Show ${span.label}`}
          onClick={() => onSpan(span)}
        />
      ))}
      {/* **Two groups, not five loose buttons.** Moving through the history is
          three gestures — back, forward, and back to today — and changing how
          much of it is on screen is two; drawn as five separate keys the strip
          reads as a row of unrelated controls over the price action it is
          sitting on. Jump-to-latest joins the pan group because it is where
          panning right ends up. */}
      <FloatingGroup>
        <FloatingButton label="‹" title="Pan back in time" aria-label="Pan left" disabled={!ready} grouped onClick={() => onPan(-1)} />
        <FloatingButton label="›" title="Pan forward in time" aria-label="Pan right" disabled={!ready} grouped onClick={() => onPan(1)} />
        <FloatingButton label="›|" title="Jump to the latest bar" aria-label="Jump to latest" disabled={!ready} grouped onClick={onLatest} />
      </FloatingGroup>
    </div>
  );
}

/**
 * The watermark behind the candles: what this chart is, in one glance.
 *
 * `AXTI, 1D` over `Technology · Semiconductors` — the symbol and interval are
 * in the toolbar above and the sector is in the data panel, but both of those
 * are chrome, and on a four-chart layout the question "which one am I looking
 * at" is asked of the *chart*, not of the strip above it. Every charting tool
 * answers it the same way and in the same place.
 *
 * **Not an annotation.** It is drawn here rather than through
 * `AnnotationTools` so that nothing can select, drag, restyle or delete it: it
 * describes the chart rather than marking a price, and a watermark that can be
 * dragged into the corner is furniture nobody asked to maintain.
 * `pointerEvents: none` keeps the band under it pannable, the same bargain the
 * span controls make.
 *
 * Centred, faint, and **under everything**: `zIndex: 2` puts it over the canvas
 * and beneath the value legend, the span controls and the pane dividers, so it
 * can never take a click or cover a reading.
 */
function ChartWatermark({
  symbol,
  frequency,
  priceBandTop,
  priceBandBottom,
}: {
  symbol: string;
  frequency: Frequency;
  /** The price pane's own edges, as percentages of the chart's height. */
  priceBandTop: number;
  priceBandBottom: number;
}) {
  // A ratio has no company behind it, so nothing is asked for — the same guard
  // `symbol-views` makes before this lookup. The cache is shared with the data
  // panel, so a chart costs a request only for a symbol nothing has asked about.
  // An index (`~TDe.`) has no company either.
  const profile = useCompanyProfile(isComposite(symbol) || isIndexSymbol(symbol) ? "" : symbol);
  const where = [profile?.sector, profile?.industry].filter(Boolean).join(" · ");
  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        // Centred at the top of the **price pane**, under the pane-reorder
        // strip. `PANE_TOOLBAR_BOTTOM` rather than a number picked by eye —
        // the same constant the annotation rail offsets by, so three things
        // wanting that quiet edge cannot land on each other when the strip
        // changes size.
        //
        // Measured from the band's own top, not the container's. They are the
        // same number only while price is pane 0, and the reorder strip's ⌃ can
        // put volume above it — which drew this over the volume pane, the very
        // mistake `priceBottomFraction` records for the span controls.
        left: 0,
        right: 0,
        top: `calc(${priceBandTop}% + ${PANE_TOOLBAR_BOTTOM + 10}px)`,
        maxHeight: `calc(${priceBandBottom - priceBandTop}% - ${PANE_TOOLBAR_BOTTOM + 10}px)`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 1,
        pointerEvents: "none",
        userSelect: "none",
        zIndex: 2,
        color: "var(--qf-text)",
        // Faint enough to read the candles through, dark enough to read at a
        // glance. A token rather than a grey, so it follows a theme switch.
        //
        // 0.11 shipped first and the owner could barely see it; 0.24 was still
        // short of what they wanted on either theme, so 0.34. One number serves
        // both modes — "darker on White, lighter on Dark" is the same request
        // twice, because the token is `text` against its own ground either way.
        // That is what using a token rather than a grey buys.
        opacity: 0.34,
        overflow: "hidden",
      }}
    >
      <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.05, letterSpacing: "0.01em" }}>
        {`${symbolTitle(symbol)}, ${intervalLabel(frequency)}`}
      </div>
      {where && <div style={{ fontSize: 13, fontWeight: 600 }}>{where}</div>}
    </div>
  );
}

/**
 * The pill the pan and zoom keys sit in: one border round the group, with the
 * buttons themselves borderless and separated by a hairline.
 *
 * `pointerEvents: auto` here rather than on each button, because the seam
 * between two of them is part of the pill and a drag starting on it should not
 * reach the canvas and pan the chart twice over.
 */
function FloatingGroup({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        border: "1px solid var(--qf-border)",
        borderRadius: 4,
        overflow: "hidden",
        pointerEvents: "auto",
      }}
    >
      {children}
    </div>
  );
}

/** A magnifier with a + or a − in it, drawn at the label sizes around it. */
function ZoomIcon({ sign }: { sign: "in" | "out" }) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="7" cy="7" r="4.6" />
      <path d="M10.4 10.4L14 14" />
      <path d="M5 7h4" />
      {sign === "in" && <path d="M7 5v4" />}
    </svg>
  );
}

/** One of the floating buttons: transparent at rest, legible on hover. */
function FloatingButton({
  label,
  title,
  disabled,
  grouped,
  onClick,
  ...rest
}: {
  /** A word, a glyph, or an icon — the zoom keys are drawn rather than typed. */
  label: React.ReactNode;
  title: string;
  disabled?: boolean;
  /** Inside a {@link FloatingGroup}: no border of its own, a seam instead. */
  grouped?: boolean;
  onClick: () => void;
} & { "aria-label"?: string }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      {...rest}
      style={{
        pointerEvents: "auto",
        font: "inherit",
        fontSize: 11,
        lineHeight: 1,
        display: "flex",
        alignItems: "center",
        padding: grouped ? "5px 8px" : "5px 9px",
        borderRadius: grouped ? 0 : 4,
        // In a group the pill draws the border; each key after the first keeps
        // a hairline so the three stay tellable apart under the pointer.
        border: grouped ? "none" : "1px solid var(--qf-border)",
        borderLeft: grouped ? "1px solid var(--qf-border)" : undefined,
        background: hovered && !disabled ? "var(--qf-surface)" : "transparent",
        color: hovered && !disabled ? "var(--qf-text)" : "var(--qf-text-secondary)",
        cursor: disabled ? "default" : "pointer",
        // Unobtrusive at rest, because it is sitting on top of the price action
        // it is there to move. A disabled one fades further rather than
        // disappearing — a control nobody can see is one nobody can ask about.
        //
        // 0.7 rather than the 0.45 this started at, which was tuned against a
        // dark canvas and did not survive the White theme: `text.secondary`
        // (#55606f) at 0.45 on white is #b2b2b2, a 1.9:1 contrast — the labels
        // were legible only as shapes. 0.7 puts them at ~3.5:1 and still leaves
        // the strip quieter than the chrome around it.
        opacity: disabled ? 0.25 : hovered ? 1 : 0.7,
        transition: "opacity 120ms, background 120ms, color 120ms",
      }}
    >
      {label}
    </button>
  );
}


/**
 * The chart's real bars as columns, for Bar Intelligence.
 *
 * From the inspector's snapshot (`dataRef`), which the realtime path keeps in
 * step bar by bar — the one copy of the OHLC this component holds. Cut to the
 * real bars: past the settled edge every price column is null.
 */
function chartBars(
  data: ChartDataSnapshot | null,
  real: number,
): { t: number[]; o: number[]; h: number[]; l: number[]; c: number[] } {
  const empty = { t: [], o: [], h: [], l: [], c: [] };
  if (!data) return empty;
  const o = data.columns["open"];
  const h = data.columns["high"];
  const l = data.columns["low"];
  const c = data.columns["close"];
  if (!h || !l || !c) return empty;
  const n = Math.min(real, data.x.length, h.length, l.length, c.length);
  const out = { t: [] as number[], o: [] as number[], h: [] as number[], l: [] as number[], c: [] as number[] };
  for (let i = 0; i < n; i++) {
    const hi = h[i];
    const lo = l[i];
    const cl = c[i];
    if (typeof hi !== "number" || typeof lo !== "number" || typeof cl !== "number") continue;
    if (!Number.isFinite(hi) || !Number.isFinite(lo) || !Number.isFinite(cl)) continue;
    out.t.push(data.x[i]!);
    // The open is only read to price a gap through a stop entry; a missing one
    // is NaN, which `followSetup` reads as "fill at the entry".
    const op = o?.[i];
    out.o.push(typeof op === "number" ? op : NaN);
    out.h.push(hi);
    out.l.push(lo);
    out.c.push(cl);
  }
  return out;
}
